import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import mascotImg from '../assets/genie-appee.png';
import { getFallbackAnalyticsInsights } from '../utils/aiContext';
import { contactAge } from '../utils/dateUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, ComposedChart, ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { useApp } from '../context/AppContext';
import { useContactPreview } from '../context/ContactPreviewContext';
import { api } from '../services/api';
import { Card, CardHeader, StatCard } from '../components/UI/Card';
import Badge, { ScoreBadge } from '../components/UI/Badge';
import Button from '../components/UI/Button';
import { getAvatarColor } from '../utils/avatar';
import { StrategicDashboard } from './Strategy';
import GenieTip from '../components/GenieTip';
import SectionHint from '../components/SectionHint';
import './Analytics.css';

const VIOLET = '#5A35FF';
const YELLOW = '#FFC400';
const GREEN  = '#10B981';
const PINK   = '#FF4FA3';
const RED    = '#EF4444';
const CYAN   = '#06B6D4';
const COLORS = [VIOLET, YELLOW, GREEN, PINK, CYAN, '#F59E0B'];

/* ── Shared Recharts tooltip style ── */
const TOOLTIP_STYLE = {
  borderRadius: 14,
  border: 'none',
  boxShadow: '0 8px 32px rgba(0,0,0,.14)',
  fontSize: 13,
  fontFamily: 'inherit',
  padding: '10px 14px',
  background: '#fff',
};

/* ── Shared chart CartesianGrid props ── */
const GRID_PROPS = { strokeDasharray: '3 3', stroke: '#EEF0F8', vertical: false };


/* ─────────────────────────────────────────────── */
/* PERSONA AVATAR — SVG dynamique                  */
/* ─────────────────────────────────────────────── */
/* PERSONA AVATAR — modern flat illustration       */
/* ─────────────────────────────────────────────── */
function PersonaAvatar({ gender, age, size = 80 }) {
  const getAgeGroup = (a) => {
    if (!a) return 'adulte';
    if (a <= 28) return 'jeune';
    if (a <= 48) return 'adulte';
    if (a <= 65) return 'senior';
    return 'aine';
  };
  const ageGroup = getAgeGroup(age);
  const isF = gender === 'femme';
  const uid = `av${isF?'f':'m'}${ageGroup?.[0]}${size}`;

  // Skins
  const skinMap = { jeune: '#FDBA8C', adulte: '#F5A46A', senior: '#E89058', aine: '#D87840' };
  const skin = skinMap[ageGroup] || '#F5A46A';
  const skinShadow = ageGroup === 'jeune' ? '#E89060' : '#C06840';

  // Hair
  const hairM = { jeune: '#1A1008', adulte: '#2A1810', senior: '#787878', aine: '#CACACA' };
  const hairF = { jeune: '#B86010', adulte: '#6A2E18', senior: '#989898', aine: '#D5D5D5' };
  const hair = (isF ? hairF : hairM)[ageGroup] || '#555';

  // Palette
  const bgGrad1 = isF ? '#FBD8F0' : '#D8E8FF';
  const bgGrad2 = isF ? '#F9AEDE' : '#AECAFF';
  const shirtCol = isF ? '#E8529A' : '#4A62D0';
  const shirtCol2 = isF ? '#C83880' : '#3448B8';
  const badge = isF ? '#EC4899' : '#3B82F6';
  const hasGlasses = ageGroup === 'senior' || ageGroup === 'aine';

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: 'block', flexShrink: 0, borderRadius: '50%' }}>
      <defs>
        <radialGradient id={`${uid}bg`} cx="40%" cy="30%" r="75%">
          <stop offset="0%" stopColor={bgGrad1}/>
          <stop offset="100%" stopColor={bgGrad2}/>
        </radialGradient>
        <linearGradient id={`${uid}sh`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={shirtCol}/>
          <stop offset="100%" stopColor={shirtCol2}/>
        </linearGradient>
        <clipPath id={`${uid}cl`}><circle cx="50" cy="50" r="50"/></clipPath>
        <filter id={`${uid}sh2`}><feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#00000018"/></filter>
      </defs>

      {/* BG */}
      <circle cx="50" cy="50" r="50" fill={`url(#${uid}bg)`}/>

      {/* Shirt / body */}
      <path d={`M14,100 C18,76 32,70 50,68 C68,70 82,76 86,100 Z`}
        fill={`url(#${uid}sh)`} clipPath={`url(#${uid}cl)`}/>
      {/* Collar shadow */}
      <ellipse cx="50" cy="68" rx="10" ry="3" fill={shirtCol2} opacity="0.4" clipPath={`url(#${uid}cl)`}/>

      {/* Neck */}
      <rect x="44" y="60" width="12" height="12" rx="5" fill={skin}/>

      {/* Female side hair (behind face) */}
      {isF && <>
        <ellipse cx="27" cy="51" rx="8.5" ry="21" fill={hair}/>
        <ellipse cx="73" cy="51" rx="8.5" ry="21" fill={hair}/>
        {/* Hair curl detail */}
        <ellipse cx="22" cy="62" rx="5" ry="8" fill={hair}/>
        <ellipse cx="78" cy="62" rx="5" ry="8" fill={hair}/>
      </>}

      {/* Face */}
      <ellipse cx="50" cy="44" rx="20" ry="23" fill={skin} filter={`url(#${uid}sh2)`}/>

      {/* Ears */}
      <ellipse cx="30" cy="46" rx="4" ry="5" fill={skin}/>
      <ellipse cx="70" cy="46" rx="4" ry="5" fill={skin}/>
      <ellipse cx="30" cy="46" rx="2" ry="3" fill={skinShadow} opacity="0.2"/>
      <ellipse cx="70" cy="46" rx="2" ry="3" fill={skinShadow} opacity="0.2"/>

      {/* Male hair */}
      {!isF && <>
        <ellipse cx="50" cy="24" rx="20.5" ry="12" fill={hair}/>
        <rect x="29.5" y="24" width="41" height="13" fill={hair}/>
        {/* Hairline fade */}
        <ellipse cx="50" cy="36" rx="20" ry="2.5" fill={hair} opacity="0.5"/>
      </>}
      {/* Female hair top */}
      {isF && <>
        <ellipse cx="50" cy="22" rx="21" ry="13" fill={hair}/>
        <rect x="29" y="22" width="42" height="11" fill={hair}/>
        {/* Center parting */}
        <path d="M50 16 Q51 22 50 28" stroke={bgGrad2} strokeWidth="1.5" fill="none" opacity="0.4"/>
      </>}

      {/* Glasses frame */}
      {hasGlasses && <>
        <rect x="31" y="39" width="15" height="11" rx="5.5" fill="rgba(185,225,255,0.25)" stroke="#8899BB" strokeWidth="1.8"/>
        <rect x="54" y="39" width="15" height="11" rx="5.5" fill="rgba(185,225,255,0.25)" stroke="#8899BB" strokeWidth="1.8"/>
        <line x1="46" y1="44.5" x2="54" y2="44.5" stroke="#8899BB" strokeWidth="1.8"/>
        <line x1="27" y1="44.5" x2="31" y2="44.5" stroke="#8899BB" strokeWidth="1.5"/>
        <line x1="69" y1="44.5" x2="73" y2="44.5" stroke="#8899BB" strokeWidth="1.5"/>
      </>}

      {/* Eyes — bigger and more expressive */}
      {!hasGlasses && <>
        {/* Eye whites */}
        <ellipse cx="40.5" cy="44" rx="5" ry="5.5" fill="white"/>
        <ellipse cx="59.5" cy="44" rx="5" ry="5.5" fill="white"/>
        {/* Eye outline */}
        <ellipse cx="40.5" cy="44" rx="5" ry="5.5" fill="none" stroke="#DDD" strokeWidth="0.5"/>
        <ellipse cx="59.5" cy="44" rx="5" ry="5.5" fill="none" stroke="#DDD" strokeWidth="0.5"/>
        {/* Iris */}
        <circle cx="40.5" cy="44.5" r="3.5" fill={isF ? '#5060A8' : '#305098'}/>
        <circle cx="59.5" cy="44.5" r="3.5" fill={isF ? '#5060A8' : '#305098'}/>
        {/* Pupil */}
        <circle cx="40.5" cy="44.5" r="2" fill="#101828"/>
        <circle cx="59.5" cy="44.5" r="2" fill="#101828"/>
        {/* Shine */}
        <circle cx="42" cy="43" r="1.1" fill="white" opacity="0.9"/>
        <circle cx="61" cy="43" r="1.1" fill="white" opacity="0.9"/>
        <circle cx="41" cy="45.5" r="0.5" fill="white" opacity="0.5"/>
        <circle cx="60" cy="45.5" r="0.5" fill="white" opacity="0.5"/>
      </>}
      {hasGlasses && <>
        <ellipse cx="38.5" cy="44.5" rx="4" ry="4.2" fill="white"/>
        <ellipse cx="61.5" cy="44.5" rx="4" ry="4.2" fill="white"/>
        <circle cx="38.5" cy="44.5" r="2.8" fill={isF ? '#5060A8' : '#305098'}/>
        <circle cx="61.5" cy="44.5" r="2.8" fill={isF ? '#5060A8' : '#305098'}/>
        <circle cx="38.5" cy="44.5" r="1.5" fill="#101828"/>
        <circle cx="61.5" cy="44.5" r="1.5" fill="#101828"/>
        <circle cx="39.8" cy="43.2" r="0.9" fill="white" opacity="0.9"/>
        <circle cx="62.8" cy="43.2" r="0.9" fill="white" opacity="0.9"/>
      </>}

      {/* Female eyelashes */}
      {isF && !hasGlasses && <>
        <path d="M36 39.5 L34.5 38" stroke={hair} strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M40.5 38.5 L40.5 36.8" stroke={hair} strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M45 39.5 L46.5 38" stroke={hair} strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M55 39.5 L53.5 38" stroke={hair} strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M59.5 38.5 L59.5 36.8" stroke={hair} strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M64 39.5 L65.5 38" stroke={hair} strokeWidth="1.3" strokeLinecap="round"/>
      </>}

      {/* Eyebrows — arched and clean */}
      {isF
        ? <>
            <path d="M35.5 38.5 Q40.5 36 45.5 38" stroke={hair} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
            <path d="M54.5 38 Q59.5 36 64.5 38.5" stroke={hair} strokeWidth="1.8" fill="none" strokeLinecap="round"/>
          </>
        : <>
            <path d="M35 38 Q40.5 35.5 46 37.5" stroke={hair} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
            <path d="M54 37.5 Q59.5 35.5 65 38" stroke={hair} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          </>
      }

      {/* Nose — subtle */}
      <path d="M47.5 49.5 Q50 55 52.5 49.5" stroke={skinShadow} strokeWidth="1.3" fill="none" strokeLinecap="round" opacity="0.5"/>
      <ellipse cx="50" cy="54.5" rx="2.5" ry="1.2" fill={skinShadow} opacity="0.18"/>

      {/* Mouth — warm smile */}
      <path d="M43 59.5 Q50 66 57 59.5" stroke="#B05040" strokeWidth="2" fill="none" strokeLinecap="round"/>
      {/* Lips */}
      {isF
        ? <>
            <path d="M43.5 59.5 Q50 63.5 56.5 59.5" fill="#E87090" opacity="0.55"/>
            <path d="M46 59.5 Q50 61 54 59.5" stroke="#D06080" strokeWidth="0.8" fill="none" opacity="0.6"/>
          </>
        : <path d="M44 59.5 Q50 62.5 56 59.5" fill={skinShadow} opacity="0.2"/>
      }

      {/* Cheeks */}
      <ellipse cx="34" cy="54" rx="5.5" ry="3.5" fill={isF ? '#F9A0C0' : '#F4956A'} opacity={isF ? 0.4 : 0.25}/>
      <ellipse cx="66" cy="54" rx="5.5" ry="3.5" fill={isF ? '#F9A0C0' : '#F4956A'} opacity={isF ? 0.4 : 0.25}/>

      {/* Gender badge */}
      <circle cx="79" cy="79" r="14" fill="white" opacity="0.95"/>
      <circle cx="79" cy="79" r="12" fill={badge}/>
      <text x="79" y="83.5" textAnchor="middle" fontSize="14" fill="white" fontWeight="900" fontFamily="system-ui">{isF ? '♀' : '♂'}</text>
    </svg>
  );
}

/* ─────────────────────────────────────────────── */
/* SHARED helpers                                  */
/* ─────────────────────────────────────────────── */
function AnalyseBox({ children }) {
  return (
    <div className="ov-analyse-box">
      <span className="ov-box-icon">🔍</span>
      <span>{children}</span>
    </div>
  );
}
function ConseilBox({ children }) {
  return (
    <div className="ov-conseil-box">
      <span className="ov-box-icon">💡</span>
      <span>{children}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* CIRCULAR GAUGE helper                           */
/* ─────────────────────────────────────────────── */
function CircularGauge({ score, size = 100, color }) {
  const r    = 38;
  const circ = 2 * Math.PI * r;
  const dash = Math.min(Math.max(score, 0), 100) / 100 * circ;
  const col  = color || (score >= 80 ? GREEN : score >= 60 ? YELLOW : RED);
  const lbl  = score >= 80 ? 'Excellent' : score >= 60 ? 'Bon' : 'À améliorer';
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={r} fill="none" stroke="var(--line)" strokeWidth="7" />
        <circle cx="45" cy="45" r={r} fill="none" stroke={col} strokeWidth="7"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform="rotate(-90 45 45)" />
      </svg>
      <div style={{ position: 'absolute', textAlign: 'center', lineHeight: 1 }}>
        <div style={{ fontSize: Math.round(size * 0.27), fontWeight: 900, color: col }}>{score}</div>
        <div style={{ fontSize: Math.round(size * 0.115), color: 'var(--muted)', fontWeight: 600, marginTop: 2 }}>{lbl}</div>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'overview',    label: '🏠 Vue d\'ensemble' },
  { id: 'segments',    label: '👥 Clients' },
  { id: 'marketing',  label: '📣 Marketing' },
  { id: 'emails',     label: '📧 Emails' },
  { id: 'sms',        label: '📱 SMS' },
  { id: 'opps',       label: '💰 Opportunités' },
  { id: 'alertes',    label: '🔔 Alertes' },
];

// Âge : brique unique partagée (src/utils/dateUtils.js).
const getAge = contactAge;

/* ── Shared persona builder — used by both TabOverview and TabSegments ── */
function buildPersonas(contacts) {
  const groups = {};
  (contacts || []).forEach(c => {
    const g   = c.gender === 'madame' ? 'femme' : c.gender === 'monsieur' ? 'homme' : 'neutre';
    const age = getAge(c.birth_date);
    const ag  = !age ? 'inconnu' : age <= 30 ? '18–30' : age <= 45 ? '31–45' : age <= 60 ? '46–60' : age <= 75 ? '61–75' : '75+';
    const key = `${g}__${ag}`;
    if (!groups[key]) groups[key] = { gender: g, ageLabel: ag, count: 0, ages: [], cities: {} };
    groups[key].count++;
    if (age) groups[key].ages.push(age);
    if (c.city) groups[key].cities[c.city] = (groups[key].cities[c.city] || 0) + 1;
  });
  const total = (contacts || []).length || 1;
  return Object.values(groups)
    .sort((a, b) => b.count - a.count)
    .slice(0, 2)
    .map((g, i) => {
      const topCity = Object.entries(g.cities).sort(([, a], [, b]) => b - a)[0]?.[0] ?? '—';
      const avgAge  = g.ages.length ? Math.round(g.ages.reduce((s, a) => s + a, 0) / g.ages.length) : null;
      return {
        rank: i + 1,
        gender: g.gender,
        ageLabel: g.ageLabel,
        averageAge: avgAge,
        location: topCity,
        percentage: Math.round(g.count / total * 100),
        count: g.count,
      };
    });
}

/* ── Count-up animation hook ── */
function useCountUp(target, duration = 1200) {
  const [val, setVal] = React.useState(0);
  const raf = useRef(null);
  React.useEffect(() => {
    const start = performance.now();
    const ease  = t => 1 - Math.pow(2, -10 * t);
    const tick  = now => {
      const t = Math.min((now - start) / duration, 1);
      setVal(Math.round(ease(t) * target));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

/* ── Analytics hero stat card ── */
function AnStat({ icon, label, value, suffix = '', delay = 0 }) {
  const display = useCountUp(value, 1100);
  return (
    <div className="an-stat-card" style={{ animationDelay: `${delay}ms` }}>
      <span className="an-stat-icon">{icon}</span>
      <span className="an-stat-value">{display}{suffix}</span>
      <span className="an-stat-label">{label}</span>
    </div>
  );
}

// ── Petit lien preview contact ────────────────────────────────────────────────
function ContactPreviewLink({ id, name }) {
  const { openPreview } = useContactPreview();
  return (
    <button
      onClick={() => openPreview(id)}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', textDecoration: 'underline dotted', font: 'inherit', fontWeight: 700 }}
    >
      {name}
    </button>
  );
}

export default function Analytics() {
  const { analytics: rawAnalytics, contacts, tenant, companyProfile } = useApp();
  // Helper: coerce value to number, fallback to 0 if null/undefined/NaN
  const n = (v) => (typeof v === 'number' && isFinite(v) ? v : (parseFloat(v) || 0));

  const raw = rawAnalytics ?? {};
  const analytics = {
    // Always-safe numeric fields
    crm_health:           n(raw.crm_health),
    total_contacts:       n(raw.total_contacts),
    active_contacts:      n(raw.active_contacts),
    active_rate:          n(raw.active_rate),
    email_rate:           n(raw.email_rate),
    birthdate_rate:       n(raw.birthdate_rate),
    phone_rate:           n(raw.phone_rate),
    open_rate_month:      n(raw.open_rate_month),
    emails_sent_month:    n(raw.emails_sent_month),
    emails_opened_month:  n(raw.emails_opened_month),
    feedback_month:       n(raw.feedback_month),
    avg_engagement:       n(raw.avg_engagement),
    dormant_contacts:     n(raw.dormant_contacts),
    vip_contacts:         n(raw.vip_contacts),
    rgpd_rate:            n(raw.rgpd_rate),
    rgpd_count:           n(raw.rgpd_count),
    consent_rate:         n(raw.consent_rate ?? raw.rgpd_rate),
    civilite_rate:        n(raw.civilite_rate),
    // Always-safe arrays
    geo_distribution:      Array.isArray(raw.geo_distribution)      ? raw.geo_distribution      : [],
    category_distribution: Array.isArray(raw.category_distribution) ? raw.category_distribution : [],
    monthly_emails:        Array.isArray(raw.monthly_emails)        ? raw.monthly_emails        : [],
    gender_distribution:   Array.isArray(raw.gender_distribution)   ? raw.gender_distribution   : [],
    region_distribution:   Array.isArray(raw.region_distribution)   ? raw.region_distribution   : [],
  };
  // Deep-link : /analytics?tab=bi ouvre directement l'onglet demandé
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.some(x => x.id === t) ? t : 'overview';
  });
  const [showStrategicDash, setShowStrategicDash] = useState(false);
  const [aiReport, setAiReport]     = useState(null);
  const [aiLoading, setAiLoading]   = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);

  const handleGenerateAiReport = async () => {
    setAiLoading(true);
    setShowAiModal(true);
    try {
      const res = await api.getMonthlyReport();
      setAiReport(res);
    } catch {
      setAiReport({ narrative: 'Impossible de générer le rapport IA. Vérifiez votre connexion ou réessayez plus tard.', stats: {} });
    } finally {
      setAiLoading(false);
    }
  };

  const dormant = useMemo(() => {
    const threshold = new Date();
    threshold.setFullYear(threshold.getFullYear() - 1);
    return contacts.filter(c => !c.last_interaction || new Date(c.last_interaction) < threshold);
  }, [contacts]);
  const vip = useMemo(() => contacts.filter(c => c.category === 'VIP' || c.engagement_score > 75), [contacts]);

  const segments = useMemo(() => {
    const champions  = contacts.filter(c => c.quality_score > 80 && c.engagement_score > 60).length;
    const loyaux     = contacts.filter(c => c.quality_score > 70 && c.engagement_score >= 40 && c.engagement_score <= 60).length;
    const potentiels = contacts.filter(c => c.quality_score >= 50 && c.quality_score <= 70 && c.status === 'active').length;
    const nouveaux   = contacts.filter(c => {
      if (!c.last_interaction) return false;
      const d = new Date(c.last_interaction);
      return (new Date() - d) < 90 * 24 * 3600 * 1000 && c.quality_score < 50;
    }).length;
    const aRisque    = contacts.filter(c => c.engagement_score < 30 && c.status === 'active').length;
    const perdus     = contacts.filter(c => c.status === 'inactive').length;
    return { champions, loyaux, potentiels, nouveaux, aRisque, perdus };
  }, [contacts]);

  const milestones = useMemo(() => {
    const now = new Date();
    return contacts.filter(c => {
      const age = getAge(c.birth_date);
      if (!age || !c.birth_date) return false;
      const nextAge = age + 1;
      if (nextAge % 10 !== 0 || nextAge < 50) return false;
      const [, m, d] = c.birth_date.split('-').map(Number);
      const bday = new Date(now.getFullYear(), m - 1, d);
      if (bday < now) bday.setFullYear(now.getFullYear() + 1);
      const diffDays = Math.round((bday - now) / (1000 * 3600 * 24));
      return diffDays <= 90;
    }).slice(0, 8);
  }, [contacts]);

  const missingEmail = useMemo(() => contacts.filter(c => !c.email && c.status === 'active').length, [contacts]);

  // ── Lifted from TabOverview for hero rendering ──────────────────────────
  const hasAiCredits = (tenant?.ai_used ?? 0) < (tenant?.ai_limit ?? Infinity);
  const [aiInsights, setAiInsights] = useState(null);

  useEffect(() => {
    // Crédits épuisés → fallback basé sur les données locales
    if (!hasAiCredits) {
      setAiInsights(getFallbackAnalyticsInsights(companyProfile, contacts));
      return;
    }

    const today       = new Date().toISOString().slice(0, 10);
    const cacheKey    = `appee_ai_insights_v2_${tenant?.id}`;
    const cacheVerKey = `appee_ai_insights_ver_${tenant?.id}`;
    // Inclure le profil entreprise dans la version → invalide si le profil change
    const profileSig  = `${companyProfile?.name || ''}|${companyProfile?.sector || ''}`;
    const version     = `${analytics.total_contacts}|${analytics.emails_sent_month}|${today}|${profileSig}`;

    try {
      const cachedVer = localStorage.getItem(cacheVerKey);
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedVer === version && cachedRaw) {
        const parsed = JSON.parse(cachedRaw);
        if (parsed && typeof parsed === 'object') { setAiInsights(parsed); return; }
      }
    } catch (_) {}

    api.getAnalyticsInsights()
      .then(data => {
        const result = typeof data === 'object' ? data : {};
        setAiInsights(result);
        try {
          localStorage.setItem(cacheKey,    JSON.stringify(result));
          localStorage.setItem(cacheVerKey, version);
        } catch (_) {}
      })
      .catch(() => setAiInsights(getFallbackAnalyticsInsights(companyProfile, contacts)));
  }, [hasAiCredits, tenant?.id, analytics.total_contacts, analytics.emails_sent_month, companyProfile?.name, companyProfile?.sector]); // eslint-disable-line

  const upcomingBdays = useMemo(() => {
    const now  = new Date();
    const in30 = new Date(now.getTime() + 30 * 24 * 3600 * 1000);
    return contacts.filter(c => {
      if (!c.birth_date) return false;
      const [, m, d] = c.birth_date.split('-').map(Number);
      const bday = new Date(now.getFullYear(), m - 1, d);
      if (bday < now) bday.setFullYear(now.getFullYear() + 1);
      return bday <= in30;
    }).length;
  }, [contacts]);

  const overviewBannerText = aiInsights === null
    ? 'Analyse en cours…'
    : (aiInsights?.overview_summary ?? `${analytics.total_contacts} contacts analysés — Score CRM ${analytics.crm_health}/100 (${analytics.crm_health >= 80 ? 'excellent' : analytics.crm_health >= 60 ? 'bon' : 'à améliorer'}). ${upcomingBdays > 0 ? `${upcomingBdays} anniversaire${upcomingBdays > 1 ? 's' : ''} dans les 30 prochains jours.` : analytics.dormant_contacts > 0 ? `${analytics.dormant_contacts} contacts dormants à réactiver.` : 'Base active — aucune urgence aujourd\'hui !'}`);

  const opportunities = useMemo(() => [
    { id: 'vip', icon: '⭐', color: YELLOW, bg: 'linear-gradient(135deg,#FFFBEB,#FEF3C7)', border: '#FCD34D', title: 'Programme VIP', desc: `${vip.length} clients VIP identifiés — valeur élevée`, value: vip.length * 200, action: 'Créer programme VIP', link: '/strategy', detail: 'CHF 200 / client estimé' },
    { id: 'reactivation', icon: '💤', color: VIOLET, bg: 'linear-gradient(135deg,#EDE9FF,#DDD6FE)', border: '#A78BFA', title: 'Réactiver les dormants', desc: `${dormant.length} clients sans interaction 12m+`, value: dormant.length * 50, action: 'Campagne réactivation', link: '/campaigns/new', detail: 'CHF 50 / client estimé' },
    { id: 'email', icon: '📧', color: GREEN, bg: 'linear-gradient(135deg,#ECFDF5,#D1FAE5)', border: '#6EE7B7', title: 'Collecter les e-mails', desc: `${missingEmail} clients actifs sans e-mail`, value: missingEmail * 30, action: 'Incentive collecte', link: '/contacts', detail: 'CHF 30 / client estimé' },
    { id: 'birthday', icon: '🎂', color: PINK, bg: 'linear-gradient(135deg,#FFF0F6,#FCE7F3)', border: '#F9A8D4', title: 'Anniversaires ronds 50+', desc: `${milestones.length} occasions premium dans 90 jours`, value: milestones.length * 150, action: 'Voir les anniversaires', link: '/birthdays', detail: 'CHF 150 / occasion estimé' },
  ].filter(o => o.value > 0), [vip, dormant, missingEmail, milestones]);

  const totalOpportunity = opportunities.reduce((s, o) => s + o.value, 0);

  // ── Banner par onglet (après opportunities + totalOpportunity) ────────────
  const heroBanner = useMemo(() => {
    const loading = aiInsights === null;
    switch (tab) {
      case 'overview':  return { title: "Vue d'ensemble de votre CRM",        text: overviewBannerText,  link: '/strategy',      linkLabel: 'Voir ma stratégie →' };
      case 'segments':  return { title: 'Synthèse de votre clientèle',        text: loading ? 'Analyse en cours…' : (aiInsights?.segments_summary  ?? `${analytics.total_contacts} contacts — ${analytics.vip_contacts} VIP, ${analytics.dormant_contacts} dormants à réactiver. Segmentez pour maximiser vos actions.`), link: '/strategy', linkLabel: 'Voir mes recommandations →' };
      case 'marketing': return { title: 'Performance de vos campagnes',       text: loading ? 'Analyse en cours…' : (aiInsights?.marketing_summary ?? `Taux d'ouverture de ${analytics.open_rate_month}% ce mois — ${analytics.open_rate_month >= 35 ? 'au-dessus' : 'en-dessous'} de la moyenne secteur (35%). ${analytics.emails_sent_month} e-mails envoyés.`), link: '/campaigns/new', linkLabel: 'Créer une campagne →' };
      case 'emails':    return { title: 'Analyse de vos e-mails',             text: loading ? 'Analyse en cours…' : (aiInsights?.emails_summary    ?? `${analytics.emails_sent_month || 0} e-mails envoyés ce mois — ${analytics.open_rate_month || 0}% de taux d'ouverture (moy. secteur 35%).`), link: '/campaigns', linkLabel: 'Voir mes campagnes →' };
      case 'opps':      return { title: 'Opportunités identifiées par Appee', text: opportunities.length > 0 ? `${opportunities.length} opportunité${opportunities.length !== 1 ? 's' : ''} identifiée${opportunities.length !== 1 ? 's' : ''} — potentiel estimé CHF ${totalOpportunity.toLocaleString()}. Priorisez les actions à impact immédiat.` : 'Toutes les opportunités ont été traitées — excellent travail !', link: '/strategy', linkLabel: 'Voir ma stratégie →' };
      default: return null;
    }
  }, [tab, overviewBannerText, aiInsights, analytics, opportunities, totalOpportunity]);

  return (
    <div className="analytics-page">
      {/* ── Modal Rapport IA ── */}
      {showAiModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 680, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>✨ Rapport stratégique IA — {new Date().toLocaleString('fr-CH', { month: 'long', year: 'numeric' })}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3 }}>Analyse personnalisée générée par Appee Intelligence</div>
              </div>
              <button onClick={() => setShowAiModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {aiLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✨</div>
                  <div style={{ fontWeight: 600 }}>Appee analyse votre base...</div>
                  <div style={{ fontSize: 13, marginTop: 6 }}>Je prépare votre rapport stratégique personnalisé</div>
                </div>
              ) : aiReport ? (
                <div>
                  {/* Stats pills */}
                  {aiReport.stats && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
                      {[
                        { icon: '📧', label: 'E-mails envoyés', value: aiReport.stats.emails_sent ?? 0 },
                        { icon: '👁️', label: "Taux d'ouverture", value: `${aiReport.stats.open_rate ?? 0}%` },
                        { icon: '👍', label: 'Feedbacks positifs', value: aiReport.stats.feedbacks ?? 0 },
                      ].map(s => (
                        <div key={s.label} style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px', textAlign: 'center', border: '1px solid var(--line)' }}>
                          <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
                          <div style={{ fontSize: 20, fontWeight: 900 }}>{s.value}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Narrative */}
                  <div style={{ background: 'var(--violet-pale)', border: '1px solid var(--violet)', borderRadius: 12, padding: '16px 20px' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 22, flexShrink: 0 }}>🤖</span>
                      <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{aiReport.narrative}</div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="secondary" onClick={() => setShowAiModal(false)}>Fermer</Button>
              {!aiLoading && <Button onClick={handleGenerateAiReport}>🔄 Régénérer</Button>}
            </div>
          </div>
        </div>
      )}

      {/* ── Hero : dark gradient + stats + tabs + conseil ── */}
      <div className="analytics-hero">
        {/* Nebula glow */}
        <div className="an-nebula" />

        {/* Title row */}
        <div className="an-hero-top">
          <div className="an-hero-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6"  y1="20" x2="6"  y2="14"/>
            </svg>
          </div>
          <div className="an-hero-title-block">
            <h1 className="an-hero-title">Analytics &amp; Insights</h1>
            <p className="an-hero-sub">Votre accompagnant marketing et vente — analyse complète de votre base</p>
          </div>
          <button className="an-hero-ai-btn" onClick={handleGenerateAiReport}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" fill="currentColor"/>
            </svg>
            Rapport IA
          </button>
        </div>

        <GenieTip
          id="analytics"
          title="📊 Tes chiffres, en clair"
          foot={<>Chaque chiffre est cliquable pour passer directement à l'action.</>}
        >
          Pas de jargon : Appee te montre ce qui marche, ce qui dort, et où est l'argent à aller chercher dans ta base.
        </GenieTip>

        {/* Stat cards */}
        <div className="an-stats-row">
          <AnStat icon="👥" label="Total contacts"    value={analytics.total_contacts}    delay={0}   />
          <AnStat icon="💯" label="Score CRM"         value={analytics.crm_health}        suffix="/100" delay={60}  />
          <AnStat icon="📧" label="Emails ce mois"    value={analytics.emails_sent_month} delay={120} />
          <AnStat icon="📈" label="Taux d'ouverture"  value={analytics.open_rate_month}   suffix="%" delay={180} />
        </div>

        {/* Tabs */}
        <div className="analytics-tabs" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', paddingBottom: 2 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              className={`analytics-tab ${tab === t.id ? 'analytics-tab-active' : ''}`}
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

      </div>

      {/* ── Banner conseil génie — en dessous du hero ── */}
      {heroBanner && (
        <div className="an-conseil-banner">
          <img src={mascotImg} alt="Appee" className="an-conseil-mascot" />
          <div className="an-conseil-body">
            <div className="an-conseil-title">{heroBanner.title}</div>
            <div className="an-conseil-text">{heroBanner.text}</div>
          </div>
          <Link to={heroBanner.link} className="an-conseil-btn">{heroBanner.linkLabel}</Link>
        </div>
      )}

      {tab === 'overview'   && <TabOverview analytics={analytics} segments={segments} opportunities={opportunities} contacts={contacts} tenant={tenant} aiInsights={aiInsights} upcomingBdays={upcomingBdays} />}
      {tab === 'segments'   && <TabSegments analytics={analytics} segments={segments} vip={vip} contacts={contacts} tenant={tenant} aiInsights={aiInsights} />}
      {tab === 'marketing'  && <TabMarketing analytics={analytics} contacts={contacts} tenant={tenant} aiInsights={aiInsights} />}
      {tab === 'emails'     && <TabEmails analytics={analytics} tenant={tenant} aiInsights={aiInsights} />}
      {tab === 'sms'        && <TabSms tenant={tenant} />}
      {tab === 'opps'       && <TabOpportunites opportunities={opportunities} milestones={milestones} totalOpportunity={totalOpportunity} tenant={tenant} companyProfile={companyProfile} contacts={contacts} analytics={analytics} aiInsights={aiInsights} />}
      {tab === 'alertes'    && <TabAlertes analytics={analytics} tenant={tenant} />}

      {showStrategicDash && (
        <StrategicDashboard
          dormant={dormant}
          vip={vip}
          onClose={() => setShowStrategicDash(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* TAB 1 — Vue d'ensemble (4 dashboards)           */
/* ─────────────────────────────────────────────── */
/* ─────────────────────────────────────────────── */
/* AI INSIGHT BLOCK — remplace AnalyseBox/ConseilBox */
/* ─────────────────────────────────────────────── */
function AiInsightBlock({ insights, cardKey, fallbackAnalyse, fallbackConseil, fallbackLink, fallbackLinkLabel }) {
  const data = insights?.[cardKey];
  const loading = insights === null; // null = en chargement, {} = chargé mais vide

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {[0.7, 0.5].map((w, i) => (
          <div key={i} style={{
            height: 14, borderRadius: 6, background: 'var(--line)',
            width: `${w * 100}%`,
            animation: 'aiPulse 1.5s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </div>
    );
  }

  const analyse = data?.analyse || fallbackAnalyse;
  const conseil = data?.conseil || fallbackConseil;
  const link    = data?.link    || fallbackLink;
  const label   = data?.link_label || fallbackLinkLabel;

  return (
    <>
      <div className="ov-analyse-box">
        <span className="ov-box-icon">🔍</span>
        <span><strong>Analyse :</strong> {analyse}</span>
      </div>
      <div className="ov-conseil-box">
        <span className="ov-box-icon">💡</span>
        <span><strong>Conseil :</strong> {conseil}</span>
      </div>
      {link && label && (
        <Link to={link} className="ov-action-btn">{label} →</Link>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────── */
/* EMAIL TIMING CONSTANTS (shared)                  */
/* ─────────────────────────────────────────────── */
const EMAIL_DAYS  = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const EMAIL_HOURS = [6,7,8,9,10,11,12,13,14,15,16,17,18,19,20];

/* ─────────────────────────────────────────────── */
/* AI CREDIT BANNER                                 */
/* ─────────────────────────────────────────────── */
function AiCreditBanner({ used, limit }) {
  return (
    <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, background: '#FFF7ED', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 16px', marginBottom: 4 }}>
      <span style={{ fontSize: 22, flexShrink: 0 }}>💡</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Analyses affichées sans IA</div>
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          {(used ?? 0) >= (limit ?? 0) && (limit ?? 0) > 0
            ? `Vous avez utilisé tous vos crédits IA (${used}/${limit}). Les analyses ci-dessous sont statiques.`
            : `Crédits IA non disponibles — analyses statiques affichées.`}
        </div>
      </div>
      <Link to="/settings" style={{ fontSize: 12, fontWeight: 700, color: '#5A35FF', background: '#EDE9FF', borderRadius: 8, padding: '7px 12px', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
        Acheter des crédits →
      </Link>
    </div>
  );
}

function TabOverview({ analytics, segments, opportunities, contacts, tenant, aiInsights, upcomingBdays }) {

  const hasAiCredits = (tenant?.ai_used ?? 0) < (tenant?.ai_limit ?? Infinity);

  const score      = analytics.crm_health;
  const scoreColor = score >= 80 ? GREEN : score >= 60 ? YELLOW : RED;
  const missingEmail = Math.max(0, analytics.active_contacts - Math.round(analytics.active_contacts * analytics.email_rate / 100));

  const rgpdRate = analytics.consent_rate ?? analytics.rgpd_rate ?? 0;
  // Une alerte « bloquant » sur une base VIDE est absurde et anxiogène pour un
  // compte tout neuf — le consentement ne devient un sujet qu'avec des contacts.
  const rgpdKo = (analytics.total_contacts ?? 0) > 0 && rgpdRate < 20;
  const dataQualityItems = [
    { label: 'E-mails renseignés',    value: analytics.email_rate,     icon: '📧', target: 95 },
    { label: 'Téléphones renseignés', value: analytics.phone_rate,     icon: '📱', target: 75 },
    { label: 'Dates de naissance',    value: analytics.birthdate_rate, icon: '🎂', target: 80 },
    { label: 'Civilité renseignée',   value: analytics.civilite_rate,  icon: '🚻', target: 80 },
    { label: 'Consentement RGPD',     value: rgpdRate,                 icon: '🔒', target: 90, critical: true },
  ];

  const potentialItems = [
    { icon: '🎂', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', label: 'Anniversaires dans 30 jours', value: upcomingBdays,              link: '/birthdays',    action: 'Préparer les messages' },
    { icon: '⭐', color: YELLOW,    bg: '#FEF9C3', border: '#FDE68A', label: 'Contacts VIP actifs',          value: analytics.vip_contacts,     link: '/contacts',     action: 'Gérer les VIPs' },
    { icon: '💤', color: VIOLET,    bg: '#EDE9FF', border: '#A78BFA', label: 'Dormants à réactiver',         value: analytics.dormant_contacts, link: '/campaigns/new',action: 'Campagne réactivation' },
    { icon: '📧', color: GREEN,     bg: '#ECFDF5', border: '#6EE7B7', label: 'Actifs sans e-mail',           value: missingEmail,               link: '/contacts',     action: 'Collecter e-mails' },
  ].filter(p => p.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {!hasAiCredits && <AiCreditBanner used={tenant?.ai_used} limit={tenant?.ai_limit} />}

      {/* ── Actions rapides ── */}
      {potentialItems.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(potentialItems.length, 4)}, 1fr)`, gap: 10 }}>
          {potentialItems.map((p, i) => (
            <Link key={i} to={p.link} style={{ textDecoration: 'none' }}>
              <div style={{
                background: p.bg, border: `1.5px solid ${p.border}`,
                borderRadius: 14, padding: '14px 16px',
                display: 'flex', gap: 12, alignItems: 'flex-start',
                transition: 'transform .15s, box-shadow .15s', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${p.border}66`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                <span style={{ fontSize: 26, flexShrink: 0, lineHeight: 1 }}>{p.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: p.color, lineHeight: 1 }}>{p.value}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginTop: 3 }}>{p.label}</div>
                  <div style={{ fontSize: 11, color: p.color, fontWeight: 700, marginTop: 5 }}>{p.action} →</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="overview-grid">

      {/* ────────── Card 1 : Santé CRM ────────── */}
      <div className="ov-card">
        <div className="ov-card-title">💚 Santé générale CRM</div>
        <div className="ov-card-body">
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <CircularGauge score={score} size={90} />
            <div className="ov-mini-grid">
              {[
                { icon: '👥', label: 'Total',     value: analytics.total_contacts,  color: VIOLET },
                { icon: '✅', label: 'Actifs',    value: analytics.active_contacts, color: GREEN },
                { icon: '📧', label: 'Emails OK', value: Math.round(analytics.total_contacts * analytics.email_rate / 100), color: CYAN },
                { icon: '💤', label: 'Inactifs',  value: analytics.dormant_contacts, color: '#9CA3AF' },
              ].map(s => (
                <div key={s.label} className="ov-mini-stat" style={{ borderColor: s.color + '35' }}>
                  <span style={{ fontSize: 16 }}>{s.icon}</span>
                  <span className="ov-mini-val" style={{ color: s.color }}>{s.value}</span>
                  <span className="ov-mini-lbl">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 6, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${score}%`, background: `linear-gradient(90deg,${scoreColor},${scoreColor}bb)`, borderRadius: 3, transition: 'width .7s' }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor, flexShrink: 0 }}>
              {score >= 80 ? '🟢 Excellent' : score >= 60 ? '🟡 Bon' : '🔴 À améliorer'}
            </span>
          </div>
        </div>
        <AiInsightBlock
          insights={aiInsights}
          cardKey="sanite"
          fallbackAnalyse={rgpdKo
            ? `Score CRM ${score}/100 — pénalisé par le consentement RGPD à ${rgpdRate}% (seuil légal non atteint). ${analytics.total_contacts} contacts, ${analytics.active_contacts} actifs.`
            : `${analytics.total_contacts} contacts, ${analytics.active_contacts} actifs (${analytics.active_rate}%). Score CRM ${score}/100 — ${score >= 80 ? 'base en excellente santé.' : score >= 60 ? 'bonne base avec des améliorations possibles.' : 'des efforts sont nécessaires.'}`}
          fallbackConseil={rgpdKo
            ? `Priorité absolue : collectez le consentement RGPD de vos ${analytics.total_contacts} contacts pour pouvoir envoyer légalement vos campagnes.`
            : score < 70
              ? `Complète les ${missingEmail} fiches sans e-mail et les dates de naissance manquantes.`
              : `Ta base est saine ! Concentre-toi sur l'engagement actif de tes ${analytics.active_contacts} contacts.`}
          fallbackLink={rgpdKo ? "/contacts" : score < 70 ? "/contacts" : "/strategy"}
          fallbackLinkLabel={rgpdKo ? "Gérer le consentement RGPD" : score < 70 ? "Compléter les fiches contacts" : "Voir ma stratégie"}
        />
      </div>

      {/* ────────── Card 3 : Qualité des données ────────── */}
      <div className="ov-card">
        <div className="ov-card-title">📊 Qualité des données</div>
        <div className="ov-card-body">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <CircularGauge score={analytics.crm_health} size={82} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 9 }}>
              {dataQualityItems.map(item => {
                const ok   = item.value >= item.target;
                const warn = !ok && item.value >= item.target * 0.7;
                const col  = ok ? GREEN : warn ? '#F59E0B' : RED;
                const isCriticalKo = item.critical && item.value < 20 && (analytics.total_contacts ?? 0) > 0;
                return (
                  <div key={item.label} style={isCriticalKo ? { background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '6px 8px', marginLeft: -4, marginRight: -4 } : {}}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, color: isCriticalKo ? RED : '#374151' }}>
                        {item.icon} {item.label}
                        {isCriticalKo && <span style={{ marginLeft: 6, fontSize: 10, background: RED, color: '#fff', borderRadius: 10, padding: '1px 6px', fontWeight: 700 }}>⚠ Bloquant</span>}
                      </span>
                      <span style={{ color: col, fontWeight: 800 }}>{item.value}%</span>
                    </div>
                    <div style={{ height: 5, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${item.value}%`, background: col, borderRadius: 3, transition: 'width .6s' }} />
                    </div>
                    {isCriticalKo && (
                      <div style={{ fontSize: 11, color: RED, marginTop: 4, fontWeight: 600 }}>
                        Seulement {item.value}% de contacts ont donné leur consentement — les envois email sont bloqués légalement.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <AiInsightBlock
          insights={aiInsights}
          cardKey="qualification"
          fallbackAnalyse={rgpdKo
            ? `⚠️ Seulement ${rgpdRate}% de vos contacts ont donné leur consentement RGPD — sans consentement, aucun email marketing ne peut être envoyé légalement.`
            : analytics.email_rate >= 95
              ? `E-mails couverts à 100% — parfait ! Consentement RGPD : ${rgpdRate}%.`
              : `${Math.round(analytics.total_contacts * (1 - analytics.email_rate / 100))} contacts sans e-mail.${analytics.birthdate_rate < 70 ? ` ${Math.round(analytics.total_contacts * (1 - analytics.birthdate_rate / 100))} sans date de naissance.` : ''} Consentement RGPD : ${rgpdRate}%.`}
          fallbackConseil={rgpdKo
            ? `Collectez le consentement RGPD de vos contacts — ajoutez un formulaire d'opt-in ou cochez manuellement les contacts qui ont donné leur accord explicite.`
            : analytics.phone_rate < 70
              ? `Collectez les numéros manquants — ${analytics.phone_rate}% seulement. Un SMS anniversaire génère 3× plus de réponses qu'un e-mail.`
              : `Données en bonne forme. Continuez à enrichir les fiches pour maintenir ce niveau.`}
          fallbackLink="/contacts"
          fallbackLinkLabel={rgpdKo ? "Gérer le consentement RGPD" : "Améliorer les données"}
        />
      </div>

      {/* ────────── Card 4 : Potentiel immédiat ────────── */}
      <div className="ov-card">
        <div className="ov-card-title">💰 Potentiel immédiat</div>
        <div className="ov-card-body">
          {potentialItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--muted)', fontSize: 13 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
              Base au top ! Aucune alerte immédiate.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {potentialItems.map(p => (
                <Link key={p.label} to={p.link} style={{ textDecoration: 'none' }}>
                  <div className="ov-potential-item" style={{ background: p.bg, borderColor: p.border }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{p.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 12.5, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.label}</div>
                      <div style={{ fontSize: 11, color: '#6B7280' }}>{p.action} →</div>
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 900, color: p.color, flexShrink: 0 }}>{p.value}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <AiInsightBlock
          insights={aiInsights}
          cardKey="actions"
          fallbackAnalyse={upcomingBdays > 0
            ? `${upcomingBdays} anniversaire${upcomingBdays > 1 ? 's' : ''} dans les 30 prochains jours — ${upcomingBdays > 5 ? 'forte concentration, préparez vos messages en avance.' : 'préparez un message personnalisé pour ne pas rater cette occasion.'}`
            : `Aucun anniversaire imminent. Vérifiez que vos contacts ont leur date de naissance renseignée.`}
          fallbackConseil={analytics.dormant_contacts > 3
            ? `Réactivez vos ${analytics.dormant_contacts} contacts dormants avec une campagne personnalisée — potentiel estimé CHF ${(analytics.dormant_contacts * 50).toLocaleString()}.`
            : analytics.vip_contacts > 0
            ? `Chouchoutez vos ${analytics.vip_contacts} contact${analytics.vip_contacts > 1 ? 's' : ''} VIP avec un message exclusif pour renforcer leur fidélité.`
            : `Base active et engagée ! Planifiez une prochaine campagne pour maintenir le momentum.`}
          fallbackLink={upcomingBdays > 0 ? "/birthdays" : analytics.dormant_contacts > 3 ? "/campaigns/new" : "/campaigns/new"}
          fallbackLinkLabel={upcomingBdays > 0 ? "Gérer les anniversaires" : "Créer une campagne"}
        />
      </div>

      {/* ── Graphique anniversaires annuel ── */}
      <BirthdayTrendChart contacts={contacts} />

      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* BIRTHDAY TREND CHART — full width               */
/* ─────────────────────────────────────────────── */
const MONTH_LABELS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

function BirthdayTrendChart({ contacts }) {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentMonth = new Date().getMonth() + 1; // 1-12

  useEffect(() => {
    api.getBirthdayTrend()
      .then(data => {
        if (Array.isArray(data)) setTrend(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Forecast from contacts (already available client-side)
  const forecast = useMemo(() => {
    const counts = Array(12).fill(0);
    contacts.forEach(c => {
      if (!c.birth_date) return;
      const m = parseInt(c.birth_date.split('-')[1], 10) - 1;
      if (m >= 0 && m < 12) counts[m]++;
    });
    return counts;
  }, [contacts]);

  // Merge backend data with forecast
  const chartData = useMemo(() => {
    return MONTH_LABELS.map((label, i) => {
      const backend = trend?.find(t => t.month === i + 1);
      return {
        month: label,
        forecast: forecast[i],
        emails:   backend?.emails   ?? 0,
        sms:      backend?.sms      ?? 0,
        isCurrent: i + 1 === currentMonth,
      };
    });
  }, [trend, forecast, currentMonth]);

  const peakMonth = useMemo(() => {
    if (!chartData.length) return null;
    return chartData.reduce((max, d) => d.forecast > max.forecast ? d : max, chartData[0]);
  }, [chartData]);

  const totalForecast = forecast.reduce((s, v) => s + v, 0);
  const avgPerMonth   = Math.round(totalForecast / 12);
  const totalEmails   = trend?.reduce((s, t) => s + t.emails, 0) ?? 0;
  const totalSms      = trend?.reduce((s, t) => s + t.sms,    0) ?? 0;

  // Custom bar with peak highlight + current month indicator
  const CustomBar = (props) => {
    const { x, y, width, height, isCurrent, month } = props;
    const isPeak   = month === peakMonth?.month;
    const fill = isPeak ? '#5A35FF' : isCurrent ? '#F87171' : '#E0E7FF';
    return <rect x={x} y={y} width={width} height={height} rx={4} fill={fill} />;
  };

  return (
    <div className="ov-card" style={{ gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <div className="ov-card-title" style={{ marginBottom: 4 }}>📅 Anniversaires & activité — {new Date().getFullYear()}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            Distribution des anniversaires par mois · envois emails et SMS
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#5A35FF' }}>{totalForecast}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Anniversaires/an</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--muted)' }}>{avgPerMonth}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Moy./mois</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#5A35FF' }}>{peakMonth?.month ?? '—'}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pic d'activité</div>
          </div>
          {totalEmails > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#10B981' }}>{totalEmails}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Emails envoyés</div>
            </div>
          )}
          {totalSms > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#F59E0B' }}>{totalSms}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SMS envoyés</div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 13 }}>
          Chargement…
        </div>
      ) : (
        <>
          <div className="an-chart-area" style={{ margin: '0 14px' }}>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F8" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value, name) => {
                  const labels = { forecast: 'Anniversaires', emails: 'Emails envoyés', sms: 'SMS envoyés' };
                  return [value, labels[name] || name];
                }}
              />
              <Legend
                formatter={(value) => {
                  const labels = { forecast: 'Anniversaires (contacts)', emails: '📧 Emails envoyés', sms: '📱 SMS envoyés' };
                  return <span style={{ fontSize: 12 }}>{labels[value] || value}</span>;
                }}
              />
              <Bar dataKey="forecast" name="forecast" radius={[4,4,0,0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.month === peakMonth?.month ? '#5A35FF' : entry.isCurrent ? '#F87171' : '#E0E7FF'}
                  />
                ))}
              </Bar>
              <Line type="monotone" dataKey="emails" name="emails" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4, fill: '#10B981' }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="sms"    name="sms"    stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 4, fill: '#F59E0B' }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
          </div>

          <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap', padding: '0 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: '#5A35FF', display: 'inline-block' }} />
              Pic d'activité ({peakMonth?.month})
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: '#F87171', display: 'inline-block' }} />
              Mois en cours
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
              <span style={{ width: 2, height: 12, background: '#10B981', display: 'inline-block', borderRadius: 1 }} />
              Emails anniversaires envoyés
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
              <span style={{ width: 2, height: 12, background: '#F59E0B', display: 'inline-block', borderRadius: 1 }} />
              SMS anniversaires envoyés
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* APPEE MASCOT — SVG purple robot                 */
/* ─────────────────────────────────────────────── */
function AppeeMascot({ size = 72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ flexShrink: 0, display: 'block' }}>
      {/* Legs */}
      <rect x="26" y="64" width="11" height="10" rx="4" fill={VIOLET} />
      <rect x="43" y="64" width="11" height="10" rx="4" fill={VIOLET} />
      {/* Body */}
      <rect x="18" y="34" width="44" height="32" rx="11" fill={VIOLET} />
      {/* Arms */}
      <rect x="6"  y="37" width="14" height="7" rx="3.5" fill={VIOLET} />
      <rect x="60" y="37" width="14" height="7" rx="3.5" fill={VIOLET} />
      {/* Belly screen */}
      <rect x="25" y="40" width="30" height="18" rx="6" fill="#7C5CFF" />
      <rect x="28" y="43" width="10" height="3.5" rx="1.5" fill={YELLOW} opacity="0.95" />
      <rect x="28" y="49" width="16" height="2.5" rx="1.2" fill="rgba(255,255,255,.4)" />
      <rect x="28" y="53" width="11" height="2.5" rx="1.2" fill="rgba(255,255,255,.28)" />
      {/* Head */}
      <rect x="22" y="8" width="36" height="30" rx="10" fill={VIOLET} />
      {/* Antenna */}
      <rect x="38.5" y="2" width="3" height="8" rx="1.5" fill={VIOLET} />
      <circle cx="40" cy="1.5" r="3.5" fill={YELLOW} />
      {/* Eyes */}
      <circle cx="31" cy="21" r="6" fill="white" />
      <circle cx="49" cy="21" r="6" fill="white" />
      <circle cx="32.5" cy="22" r="3" fill="#1A0066" />
      <circle cx="50.5" cy="22" r="3" fill="#1A0066" />
      <circle cx="33.5" cy="21" r="1.1" fill="white" />
      <circle cx="51.5" cy="21" r="1.1" fill="white" />
      {/* Mouth */}
      <path d="M33 31 Q40 36 47 31" stroke="white" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/* ─────────────────────────────────────────────── */
/* BUBBLE FIELD — proportional bubble chart        */
/* ─────────────────────────────────────────────── */
const BUBBLE_POSITIONS = [
  { left: '38%', top: '34%' },
  { left: '17%', top: '22%' },
  { left: '63%', top: '20%' },
  { left: '14%', top: '62%' },
  { left: '67%', top: '60%' },
  { left: '40%', top: '72%' },
];
function BubbleField({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ position: 'relative', height: 190, width: '100%' }}>
      {data.slice(0, 6).map((d, i) => {
        const ratio = d.value / max;
        const size  = Math.max(44, Math.round(ratio * 100));
        const pos   = BUBBLE_POSITIONS[i] || { left: `${20 + i * 14}%`, top: '40%' };
        const color = COLORS[i % COLORS.length];
        return (
          <div key={d.label} style={{
            position: 'absolute', left: pos.left, top: pos.top,
            transform: 'translate(-50%, -50%)',
            width: size, height: size, borderRadius: '50%',
            background: `${color}1A`, border: `2.5px solid ${color}55`,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            transition: 'transform .2s',
          }}>
            <span style={{ fontSize: size > 65 ? 16 : 12, fontWeight: 900, color, lineHeight: 1 }}>{d.value}</span>
            <span style={{ fontSize: size > 65 ? 9 : 7.5, color: 'var(--muted)', fontWeight: 600, textAlign: 'center', maxWidth: size - 6, lineHeight: 1.2 }}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* SWISS MAP — accurate coords + ripple dots       */
/* ─────────────────────────────────────────────── */
// ViewBox 0 0 380 260  — lon: 5.95–10.49, lat: 45.82–47.81
// x = (lon - 5.95) * 83.7,  y = (47.81 - lat) * 131.7
// Coordonnées GPS réelles (lon, lat) — projetées sur la vraie silhouette de la Suisse
const SWISS_CITY_GPS = {
  'Genève': [6.143, 46.204], 'Geneva': [6.143, 46.204], 'Carouge': [6.139, 46.181],
  'Meyrin': [6.080, 46.234], 'Vernier': [6.100, 46.214], 'Nyon': [6.240, 46.383],
  'Gland': [6.270, 46.421], 'Rolle': [6.339, 46.458], 'Morges': [6.498, 46.511],
  'Lausanne': [6.633, 46.520], 'Renens': [6.588, 46.539], 'Vevey': [6.843, 46.462],
  'Montreux': [6.911, 46.435], 'Monthey': [6.946, 46.253], 'Martigny': [7.073, 46.103],
  'Verbier': [7.228, 46.097], 'Sion': [7.360, 46.229], 'Sierre': [7.532, 46.292],
  'Crans-Montana': [7.500, 46.312], 'Viège': [7.881, 46.294], 'Visp': [7.881, 46.294],
  'Zermatt': [7.749, 46.020], 'Bulle': [7.057, 46.619], 'Fribourg': [7.161, 46.803],
  'Payerne': [6.937, 46.822], 'Yverdon': [6.641, 46.779], 'Yverdon-les-Bains': [6.641, 46.779],
  'Neuchâtel': [6.931, 46.992], 'La Chaux-de-Fonds': [6.833, 47.100], 'Le Locle': [6.752, 47.056],
  'Bern': [7.447, 46.948], 'Berne': [7.447, 46.948], 'Köniz': [7.415, 46.925],
  'Thun': [7.628, 46.758], 'Thoune': [7.628, 46.758], 'Interlaken': [7.868, 46.686],
  'Gstaad': [7.287, 46.472], 'Biel': [7.247, 47.137], 'Bienne': [7.247, 47.137],
  'Grenchen': [7.396, 47.192], 'Soleure': [7.532, 47.208], 'Solothurn': [7.532, 47.208],
  'Delémont': [7.345, 47.365], 'Porrentruy': [7.076, 47.415], 'Moutier': [7.370, 47.278],
  'Basel': [7.588, 47.560], 'Bâle': [7.588, 47.560], 'Liestal': [7.735, 47.484],
  'Olten': [7.904, 47.350], 'Aarau': [8.044, 47.392], 'Baden': [8.306, 47.473],
  'Langenthal': [7.788, 47.212], 'Burgdorf': [7.628, 47.059], 'Lucerne': [8.310, 47.050],
  'Luzern': [8.310, 47.050], 'Kriens': [8.277, 47.035], 'Emmen': [8.301, 47.078],
  'Zug': [8.515, 47.166], 'Zoug': [8.515, 47.166], 'Schwyz': [8.654, 47.021],
  'Altdorf': [8.639, 46.880], 'Sarnen': [8.245, 46.896], 'Stans': [8.366, 46.958],
  'Zurich': [8.541, 47.374], 'Zürich': [8.541, 47.374], 'Uster': [8.717, 47.347],
  'Dietikon': [8.400, 47.402], 'Winterthur': [8.724, 47.500], 'Rapperswil': [8.818, 47.226],
  'Schaffhausen': [8.634, 47.697], 'Schaffhouse': [8.634, 47.697], 'Frauenfeld': [8.898, 47.554],
  'Kreuzlingen': [9.175, 47.646], 'Wil': [9.046, 47.463], 'St. Gallen': [9.377, 47.424],
  'St-Gall': [9.377, 47.424], 'Saint-Gall': [9.377, 47.424], 'Herisau': [9.279, 47.386],
  'Appenzell': [9.409, 47.331], 'Arbon': [9.434, 47.514], 'Glaris': [9.068, 47.040],
  'Glarus': [9.068, 47.040], 'Coire': [9.533, 46.849], 'Chur': [9.533, 46.849],
  'Davos': [9.837, 46.802], 'St-Moritz': [9.838, 46.498], 'Bellinzona': [9.024, 46.195],
  'Bellinzone': [9.024, 46.195], 'Locarno': [8.796, 46.171], 'Ascona': [8.772, 46.159],
  'Lugano': [8.952, 45.999],
};

// Projection lon/lat → pixels (constantes calées sur le tracé GeoJSON réel)
const CH = { minLon: 5.9573, maxLat: 47.8032, kx: 0.6844, scale: 244.74, pad: 22 };
const projCH = ([lon, lat]) => ({
  x: (lon - CH.minLon) * CH.kx * CH.scale + CH.pad,
  y: (CH.maxLat - lat) * CH.scale + CH.pad,
});

// Silhouette réelle de la Suisse (GeoJSON haute résolution projeté, 774 points)
const SWISS_PATH = "M458.4,22.0 L460.7,27.6 L457.8,28.3 L455.8,27.2 L443.3,31.0 L440.2,40.4 L432.1,47.1 L434.4,51.7 L432.1,54.6 L438.8,58.7 L442.0,58.7 L443.4,61.4 L443.9,59.6 L447.2,61.1 L452.5,60.6 L452.5,56.8 L454.4,57.9 L454.8,55.7 L458.4,54.5 L461.7,56.2 L464.3,54.4 L466.3,56.1 L469.9,58.3 L468.7,62.3 L465.5,62.5 L466.8,60.4 L465.8,58.9 L464.2,61.3 L464.5,65.0 L465.6,68.3 L461.1,72.8 L458.4,71.8 L460.2,68.4 L455.5,65.0 L451.1,63.7 L449.6,67.2 L443.7,69.0 L441.1,71.8 L442.3,75.6 L445.9,74.7 L446.9,76.1 L437.1,79.5 L430.4,77.6 L429.0,80.1 L418.4,78.7 L413.5,73.9 L414.0,69.9 L410.9,68.6 L408.8,69.6 L407.6,68.2 L404.1,69.0 L402.2,70.6 L400.3,66.6 L397.9,67.1 L394.4,71.0 L391.2,73.3 L388.8,73.1 L387.5,75.6 L382.1,75.9 L380.7,80.9 L378.7,82.1 L375.3,80.4 L371.1,82.6 L366.9,83.7 L363.4,82.5 L357.5,82.2 L355.7,85.3 L350.1,84.6 L348.3,81.3 L349.7,79.2 L345.2,74.3 L340.7,74.7 L338.7,76.5 L336.3,75.3 L333.6,74.5 L332.6,78.8 L329.7,82.1 L322.6,85.0 L316.5,86.2 L312.5,88.4 L308.2,87.4 L307.0,85.0 L304.7,83.5 L302.7,81.3 L305.3,81.6 L307.9,80.1 L310.2,80.4 L310.9,79.0 L311.4,80.2 L312.1,78.5 L311.1,77.7 L310.5,75.8 L309.2,75.3 L311.1,72.8 L312.9,71.6 L310.9,72.2 L309.5,73.8 L304.8,72.5 L300.4,77.3 L297.9,77.1 L297.6,75.1 L295.3,74.2 L294.5,77.8 L291.6,77.2 L289.8,78.5 L290.1,80.2 L283.2,85.1 L279.9,87.1 L280.8,89.1 L283.6,87.8 L285.4,88.5 L284.3,92.6 L283.3,91.8 L281.1,92.4 L280.1,91.0 L281.8,93.9 L282.2,96.8 L278.2,100.6 L275.6,100.9 L269.3,96.7 L267.1,100.8 L270.3,101.2 L273.2,102.9 L271.4,105.5 L268.5,106.4 L266.8,109.7 L264.0,111.9 L258.4,112.6 L252.5,110.6 L247.9,111.2 L245.8,112.4 L244.0,112.2 L237.9,115.8 L235.9,111.4 L229.6,112.0 L225.2,110.1 L228.5,99.1 L230.6,98.5 L224.7,98.8 L218.3,95.4 L215.8,97.3 L212.3,97.4 L209.6,99.1 L207.4,97.5 L200.6,95.0 L194.1,97.7 L194.6,102.8 L196.7,104.9 L197.0,107.2 L195.4,108.8 L192.0,109.0 L190.1,112.5 L186.7,112.5 L186.5,119.0 L182.3,119.4 L182.2,123.1 L177.6,126.6 L176.5,131.7 L179.7,131.0 L184.5,131.4 L193.0,130.3 L196.9,129.1 L198.8,127.5 L202.2,128.4 L205.1,130.3 L205.1,133.1 L207.0,134.4 L205.8,136.3 L204.2,138.6 L202.3,138.2 L197.9,139.5 L199.2,141.4 L198.0,144.8 L195.6,146.4 L192.2,146.0 L186.9,148.2 L188.6,153.0 L188.1,157.4 L189.3,158.9 L187.2,160.6 L184.6,163.0 L184.1,164.3 L176.5,169.5 L175.5,173.0 L170.1,176.7 L173.2,178.2 L165.8,185.1 L153.1,192.2 L154.1,195.0 L152.9,196.3 L150.6,196.2 L147.0,198.7 L145.1,201.9 L149.4,206.0 L145.5,209.7 L143.6,209.3 L138.7,212.5 L137.0,217.7 L131.6,220.6 L129.3,220.3 L123.7,223.3 L113.7,226.7 L112.4,224.6 L105.6,232.6 L101.6,236.0 L106.5,244.4 L106.3,255.1 L103.1,259.8 L103.0,263.6 L101.4,264.8 L102.2,267.6 L106.1,270.4 L104.9,273.6 L102.3,276.7 L95.6,280.1 L94.3,281.9 L91.1,284.7 L89.6,286.4 L85.1,290.1 L76.4,294.4 L74.2,296.4 L73.8,298.0 L64.8,305.9 L47.5,321.8 L55.4,329.8 L54.2,331.3 L53.4,334.1 L52.2,333.4 L48.2,338.3 L45.5,345.3 L41.3,349.3 L41.7,352.2 L43.2,353.8 L41.8,357.5 L40.1,361.3 L45.8,363.1 L46.8,365.5 L48.2,366.4 L54.3,371.0 L55.8,370.5 L56.3,371.8 L57.6,373.5 L55.9,375.9 L55.6,377.4 L54.5,378.5 L52.6,380.4 L51.2,383.8 L49.9,385.8 L49.1,389.6 L48.9,391.2 L46.3,393.6 L46.6,395.1 L48.2,396.6 L49.1,398.6 L49.9,401.8 L47.4,404.6 L45.9,405.0 L43.8,403.0 L41.3,404.1 L39.9,403.4 L37.1,406.5 L34.7,405.1 L31.9,406.7 L29.4,409.2 L27.6,408.8 L27.9,410.5 L25.6,410.4 L24.1,412.3 L24.5,413.6 L23.1,415.1 L27.8,417.6 L27.1,421.5 L25.5,423.3 L23.2,427.9 L23.4,429.6 L22.0,430.7 L24.8,430.9 L26.3,429.4 L28.2,428.0 L31.7,428.5 L34.0,429.1 L35.1,430.4 L36.4,428.6 L37.8,426.3 L41.8,426.9 L44.0,426.3 L45.9,428.1 L50.2,428.7 L53.1,427.3 L55.2,425.9 L60.6,422.6 L60.5,419.4 L62.0,418.3 L63.8,416.3 L66.0,414.6 L68.5,412.8 L70.1,413.1 L72.4,411.6 L75.7,410.6 L78.4,408.3 L81.0,403.6 L80.6,401.7 L81.1,400.5 L79.7,401.1 L78.6,400.4 L78.6,398.6 L76.9,400.7 L75.4,401.5 L74.1,402.6 L72.8,401.6 L70.9,399.1 L69.7,396.5 L69.2,394.3 L71.3,392.3 L70.6,389.3 L66.0,386.9 L71.5,375.1 L85.3,364.6 L100.4,361.6 L116.3,351.8 L143.5,352.1 L166.8,358.6 L163.5,368.6 L164.4,370.4 L161.9,373.4 L158.7,374.0 L158.3,376.2 L161.0,382.4 L162.5,381.8 L163.6,384.9 L165.3,384.8 L168.4,388.5 L171.4,392.3 L172.6,391.8 L174.3,394.7 L173.1,396.8 L172.4,400.9 L169.8,403.3 L168.4,406.1 L166.6,406.8 L166.7,409.2 L163.7,413.4 L165.2,419.1 L161.7,423.7 L161.6,425.8 L162.9,430.0 L165.8,431.5 L169.8,431.0 L171.9,432.0 L179.8,432.9 L177.2,440.0 L178.3,444.6 L176.3,446.6 L175.8,450.5 L178.2,453.1 L179.7,451.7 L182.3,450.3 L184.1,447.5 L185.9,447.4 L186.0,449.7 L187.6,450.9 L190.7,456.0 L194.3,461.6 L197.6,463.4 L200.3,468.6 L198.5,470.3 L200.0,473.1 L202.9,474.4 L202.8,478.5 L204.0,482.3 L206.7,485.2 L207.6,488.4 L209.8,490.2 L211.2,492.4 L212.8,494.7 L214.3,497.4 L217.8,497.3 L219.7,494.7 L223.0,493.1 L224.5,495.3 L228.3,498.0 L229.9,496.8 L230.2,494.1 L233.0,490.5 L236.4,490.2 L239.3,489.8 L240.1,490.9 L245.0,482.8 L250.2,483.6 L250.9,485.9 L254.4,484.0 L256.4,485.7 L261.0,488.5 L264.1,485.2 L266.1,485.2 L271.1,480.4 L273.8,479.4 L276.3,479.1 L275.6,475.4 L280.2,472.8 L283.2,473.5 L287.4,473.8 L287.1,469.1 L288.6,466.8 L293.8,466.6 L294.9,470.4 L301.4,470.6 L305.0,470.5 L307.1,469.3 L310.5,474.2 L314.9,474.5 L315.1,480.3 L318.3,481.9 L320.7,480.8 L322.3,478.0 L326.8,479.7 L331.2,483.7 L334.6,481.4 L340.8,482.8 L342.4,483.1 L343.8,481.1 L342.5,479.0 L342.4,476.1 L343.7,472.7 L343.9,469.7 L346.6,469.1 L349.2,464.0 L361.6,464.1 L364.6,460.7 L366.3,459.9 L366.3,456.0 L370.0,452.4 L368.1,445.5 L369.9,438.3 L376.8,437.4 L382.6,435.8 L383.5,431.2 L388.9,430.0 L389.3,423.4 L391.6,419.5 L389.7,414.7 L387.0,407.8 L384.9,406.9 L382.4,402.5 L377.5,399.8 L381.2,395.8 L385.0,391.2 L387.5,389.3 L391.2,391.0 L399.1,388.5 L401.7,382.0 L408.6,378.4 L408.3,375.1 L410.2,373.4 L412.4,374.2 L416.8,370.3 L417.0,366.2 L416.1,364.9 L412.6,364.0 L415.5,359.3 L418.2,359.5 L425.3,352.5 L428.5,353.2 L437.8,349.7 L442.1,354.8 L440.4,357.4 L441.1,360.9 L442.3,362.0 L441.6,364.9 L443.1,366.6 L441.7,368.6 L442.9,375.2 L441.8,376.7 L441.7,382.4 L438.0,385.3 L436.7,389.4 L440.2,398.5 L438.1,401.6 L441.8,403.5 L443.1,407.0 L446.6,407.7 L450.4,409.3 L453.3,409.8 L454.7,415.4 L457.1,417.0 L459.2,419.0 L460.7,423.7 L464.9,425.4 L463.4,428.3 L467.0,431.3 L466.9,433.2 L472.6,433.3 L475.0,436.1 L476.3,436.7 L479.1,438.5 L483.8,439.0 L485.2,436.1 L488.6,433.6 L491.2,438.6 L494.0,439.2 L497.8,439.9 L499.4,438.9 L507.0,445.6 L507.4,449.1 L505.6,450.9 L503.6,453.1 L501.8,457.8 L498.7,458.1 L495.5,465.4 L501.7,466.1 L505.3,467.6 L511.7,474.0 L514.1,473.4 L513.9,479.5 L516.2,483.8 L518.4,484.5 L519.2,490.2 L520.9,495.2 L522.2,495.7 L519.1,498.9 L517.2,504.6 L523.9,502.1 L527.0,504.3 L530.2,503.4 L531.2,507.1 L536.9,507.1 L538.4,500.9 L540.9,493.5 L544.4,490.8 L546.7,487.6 L545.1,487.6 L544.2,485.1 L540.6,482.5 L538.5,481.4 L535.3,480.9 L534.8,478.1 L533.7,476.8 L534.6,475.4 L534.4,473.5 L530.1,470.5 L530.9,467.5 L533.7,468.2 L533.2,466.2 L536.1,464.9 L534.7,458.4 L533.2,455.0 L534.8,450.9 L544.2,447.2 L546.5,442.4 L543.8,434.2 L551.6,430.0 L554.7,425.4 L558.1,423.6 L558.5,421.3 L561.9,421.9 L564.7,419.3 L564.1,415.5 L565.8,412.2 L568.4,410.9 L568.5,407.2 L571.1,406.3 L572.9,406.5 L573.8,397.7 L575.3,395.0 L579.1,390.8 L579.8,387.0 L581.9,383.5 L581.4,378.2 L578.7,374.9 L578.0,369.8 L578.7,361.7 L573.1,357.3 L573.4,354.1 L578.0,351.3 L578.9,342.0 L593.8,339.0 L593.8,343.2 L600.3,349.2 L603.0,342.3 L608.8,339.5 L608.1,360.6 L609.9,365.2 L609.0,370.5 L615.0,375.4 L622.4,387.9 L625.4,389.6 L632.5,391.4 L637.3,393.0 L644.7,390.5 L650.4,392.1 L653.6,385.4 L652.5,384.0 L653.2,379.6 L656.2,377.5 L661.5,381.0 L672.0,374.9 L678.0,373.8 L682.9,370.8 L687.5,374.0 L688.4,371.7 L691.4,370.8 L693.0,373.8 L698.7,378.0 L696.0,385.1 L699.0,386.5 L698.3,392.8 L708.4,397.5 L709.1,403.7 L706.3,408.1 L710.7,410.5 L714.8,407.9 L724.3,406.5 L728.6,401.2 L725.0,391.4 L716.7,382.8 L717.4,375.9 L721.5,374.5 L721.0,370.5 L726.3,368.4 L726.1,361.7 L720.4,358.2 L713.7,361.0 L705.9,354.5 L707.9,347.4 L706.3,338.6 L708.2,335.6 L705.9,330.9 L713.2,324.2 L713.7,322.2 L716.4,319.4 L716.0,315.5 L718.1,314.8 L730.2,310.6 L735.5,312.0 L739.0,307.8 L742.7,313.8 L740.1,318.9 L740.4,323.8 L744.3,322.4 L747.3,324.5 L749.3,328.6 L754.6,328.6 L756.7,330.5 L758.8,326.8 L765.2,330.2 L769.6,328.9 L775.6,332.8 L779.2,328.4 L780.6,313.2 L773.7,307.4 L767.3,308.1 L763.4,295.4 L766.4,290.8 L768.7,287.8 L766.4,283.4 L773.0,278.6 L770.3,269.4 L774.4,266.4 L777.8,254.1 L778.3,241.8 L782.0,234.0 L771.9,229.4 L768.2,223.9 L760.8,220.2 L755.8,222.5 L753.0,228.9 L752.6,236.7 L740.1,235.8 L737.4,250.0 L716.2,256.5 L713.0,252.3 L707.5,250.5 L702.0,245.5 L689.5,239.5 L677.6,235.3 L680.3,221.1 L678.5,216.1 L676.2,212.9 L666.1,212.0 L650.4,206.5 L640.3,203.7 L636.6,206.0 L633.4,203.7 L632.1,204.1 L630.8,204.6 L629.1,205.7 L626.3,206.1 L625.0,206.7 L624.3,204.5 L622.9,203.3 L620.6,203.2 L618.2,203.9 L615.9,204.8 L614.3,204.7 L613.0,206.6 L612.3,205.7 L610.5,202.8 L614.1,200.5 L617.9,197.6 L618.7,193.7 L616.7,183.9 L615.4,181.9 L613.9,178.7 L613.0,174.2 L613.7,170.3 L615.4,166.9 L615.6,164.7 L616.7,162.6 L618.8,158.9 L620.5,154.3 L620.4,152.3 L626.0,143.9 L632.5,138.4 L636.2,130.2 L643.1,127.5 L645.4,123.8 L641.7,117.0 L641.8,108.3 L635.7,106.5 L633.4,103.6 L632.3,105.8 L626.2,97.9 L625.9,95.1 L623.9,87.1 L618.7,87.7 L607.0,73.2 L575.0,57.1 L560.8,58.3 L558.1,55.7 L554.2,56.2 L549.1,53.1 L535.9,51.0 L524.8,56.6 L514.3,59.8 L511.0,57.8 L509.4,52.1 L506.7,51.4 L507.1,48.3 L509.1,48.0 L510.7,48.6 L510.4,46.3 L506.5,46.0 L506.0,44.5 L501.8,44.3 L502.2,42.9 L499.3,41.3 L500.1,39.8 L499.1,38.0 L497.9,39.1 L498.2,40.5 L495.8,40.6 L493.1,43.1 L493.1,45.3 L497.3,46.5 L498.0,47.8 L499.9,48.8 L498.3,49.1 L497.1,53.4 L486.1,48.6 L487.3,43.5 L484.3,41.3 L483.7,38.6 L485.2,35.9 L488.6,35.5 L484.2,31.5 L480.8,33.0 L478.5,26.3 L473.7,23.0 L472.5,27.4 L470.0,32.7 L467.7,27.9 L467.9,22.6 L460.2,22.3 L458.4,22.0 Z";

// Lacs principaux (positions GPS réelles projetées, clippés dans les frontières)
const SWISS_LAKES = [
  { cx: 121, cy: 358, rx: 58, ry: 13, rot: -12 },  // Léman
  { cx: 177, cy: 243, rx: 13, ry: 33, rot: 35 },   // Neuchâtel
  { cx: 218, cy: 194, rx: 7,  ry: 15, rot: 35 },   // Bienne
  { cx: 317, cy: 297, rx: 21, ry: 8,  rot: -25 },  // Thoune
  { cx: 357, cy: 285, rx: 17, ry: 7,  rot: -15 },  // Brienz
  { cx: 431, cy: 219, rx: 25, ry: 11, rot: -10 },  // Quatre-Cantons
  { cx: 478, cy: 165, rx: 25, ry: 8,  rot: -33 },  // Zurich
  { cx: 590, cy: 72,  rx: 44, ry: 15, rot: -18 },  // Constance
  { cx: 475, cy: 456, rx: 10, ry: 25, rot: 10 },   // Majeur
  { cx: 528, cy: 466, rx: 21, ry: 7,  rot: -20 },  // Lugano
];

function SwissMap({ cities = [] }) {
  const maxCount = Math.max(...cities.map(c => c.count), 1);

  const findGps = (name) => {
    if (SWISS_CITY_GPS[name]) return SWISS_CITY_GPS[name];
    const lower = name?.toLowerCase();
    const key = Object.keys(SWISS_CITY_GPS).find(k => k.toLowerCase() === lower);
    if (key) return SWISS_CITY_GPS[key];
    const firstWord = name?.split(' ')[0];
    const key2 = firstWord && Object.keys(SWISS_CITY_GPS).find(k => k.toLowerCase().startsWith(firstWord.toLowerCase()));
    return key2 ? SWISS_CITY_GPS[key2] : null;
  };

  const matchedCities = cities
    .map(c => { const gps = findGps(c.city); return gps ? { ...c, pos: projCH(gps) } : null; })
    .filter(Boolean);

  return (
    <div style={{ width: '100%', background: 'linear-gradient(160deg, #F7F5FF 0%, #EEF2FF 100%)', borderRadius: 14, overflow: 'hidden', border: '1px solid #E4E0F4' }}>
      <svg viewBox="0 0 804 529" style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <linearGradient id="chFill" x1="0" y1="0" x2="0.6" y2="1">
            <stop offset="0%" stopColor="#FFFFFF"/>
            <stop offset="100%" stopColor="#F1EDFF"/>
          </linearGradient>
          <filter id="chShadow" x="-6%" y="-6%" width="112%" height="112%">
            <feDropShadow dx="0" dy="7" stdDeviation="11" floodColor="#5A35FF" floodOpacity="0.16"/>
          </filter>
          <clipPath id="chClip"><path d={SWISS_PATH}/></clipPath>
        </defs>

        {/* Silhouette réelle de la Suisse */}
        <path d={SWISS_PATH} fill="url(#chFill)" stroke="#C9BCF2" strokeWidth="2" filter="url(#chShadow)"/>

        {/* Lacs (positions réelles, contenus dans les frontières) */}
        <g clipPath="url(#chClip)">
          {SWISS_LAKES.map((l, i) => (
            <ellipse key={i} cx={l.cx} cy={l.cy} rx={l.rx} ry={l.ry}
              transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
              fill="#A8CBF0" opacity="0.55"/>
          ))}
        </g>

        {/* Bulles villes */}
        {matchedCities.map((c, i) => {
          const { x, y } = c.pos;
          const ratio = c.count / maxCount;
          const rInner = 9 + ratio * 13;
          const col = i === 0 ? VIOLET : i === 1 ? '#4B82F6' : '#6B9EFF';
          return (
            <g key={c.city}>
              <circle cx={x} cy={y} r={rInner * 4.4} fill={col} opacity="0.05"/>
              <circle cx={x} cy={y} r={rInner * 2.8} fill={col} opacity="0.09"/>
              <circle cx={x} cy={y} r={rInner * 1.7} fill={col} opacity="0.17"/>
              <circle cx={x} cy={y} r={rInner} fill={col} opacity="0.92"/>
              <circle cx={x} cy={y} r={rInner * 0.4} fill="white"/>
              <text x={x} y={y + rInner + 21} textAnchor="middle" fontSize="17" fontWeight="800"
                fill="#2b2350" stroke="#ffffff" strokeWidth="4" paintOrder="stroke"
                fontFamily="Poppins, system-ui">{c.city}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* GENDER PIE CHART                                */
/* ─────────────────────────────────────────────── */
function GenderPieChart({ contacts }) {
  const data = useMemo(() => {
    const f = contacts.filter(c => c.gender === 'madame').length;
    const m = contacts.filter(c => c.gender === 'monsieur').length;
    const t = contacts.length || 1;
    return [
      { name: 'Femme', value: f, pct: Math.round(f / t * 100), color: '#EC4899', symbol: '♀' },
      { name: 'Homme', value: m, pct: Math.round(m / t * 100), color: '#3B82F6', symbol: '♂' },
    ].filter(d => d.value > 0);
  }, [contacts]);

  if (data.length === 0) return (
    <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)', fontSize: 13 }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>⚧</div>Renseignez la civilité pour voir la répartition
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div className="an-chart-area" style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3} strokeWidth={0}>
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${v} contacts`, n]} contentStyle={TOOLTIP_STYLE} />
        </PieChart>
      </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', gap: 24, justifyContent: 'center' }}>
        {data.map(d => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20, color: d.color, fontWeight: 900 }}>{d.symbol}</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--ink)' }}>{d.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: d.color, color: 'white', fontWeight: 900, fontSize: 12, borderRadius: 6, padding: '1px 8px' }}>{d.value}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: d.color }}>{d.pct}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* AGE PYRAMID CHART                               */
/* ─────────────────────────────────────────────── */
function AgePyramidChart({ contacts }) {
  const data = useMemo(() => {
    const bands = [
      { range: '0–17', min: 0,  max: 17  },
      { range: '18–25', min: 18, max: 25 },
      { range: '26–35', min: 26, max: 35 },
      { range: '36–45', min: 36, max: 45 },
      { range: '46–55', min: 46, max: 55 },
      { range: '56–65', min: 56, max: 65 },
      { range: '66–75', min: 66, max: 75 },
      { range: '75+',   min: 76, max: 999 },
    ];
    const counts = bands.map(b => ({ ...b, count: 0 }));
    contacts.forEach(c => {
      const age = getAge(c.birth_date);
      if (!age) return;
      const b = counts.find(b => age >= b.min && age <= b.max);
      if (b) b.count++;
    });
    return counts;
  }, [contacts]);

  const maxVal = Math.max(...data.map(d => d.count), 1);
  const topBand = data.reduce((m, d) => d.count > m.count ? d : m, data[0]);

  return (
    <div>
      <div className="an-chart-area">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
          <CartesianGrid horizontal={false} stroke="#EEF0F8" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} label={{ value: 'Nombre de contacts', position: 'insideBottom', offset: -2, fontSize: 10, fill: 'var(--muted)' }} />
          <YAxis type="category" dataKey="range" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={46} />
          <Tooltip formatter={(v) => [`${v} contacts`]} cursor={{ fill: 'rgba(90,53,255,.06)' }} contentStyle={TOOLTIP_STYLE} />
          <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={22}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.range === topBand.range ? VIOLET : `${VIOLET}60`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </div>
      {topBand.count > 0 && (
        <div style={{ background: `${GREEN}12`, border: `1px solid ${GREEN}40`, borderRadius: 10, padding: '10px 14px', marginTop: 4 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: GREEN }}>📊 Analyse démographique</div>
          <div style={{ fontSize: 12, marginTop: 4, color: 'var(--ink)' }}>
            Votre cœur de cible : <strong>{topBand.range} ans</strong> ({topBand.count} contacts).
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* GENERATIONAL CLUSTERS — ScatterChart            */
/* ─────────────────────────────────────────────── */
function GenerationalClusters({ contacts }) {
  const [expandedClusters, setExpandedClusters] = useState({});
  const toggleCluster = (i) => setExpandedClusters(prev => ({ ...prev, [i]: !prev[i] }));

  const { clusters, topClusters } = useMemo(() => {
    const map = {};
    contacts.forEach(c => {
      if (!c.city || !c.birth_date) return;
      const age = getAge(c.birth_date);
      if (!age || age < 15 || age > 100) return;
      const band = age < 25 ? '20–24' : age < 30 ? '25–29' : age < 35 ? '30–34' : age < 40 ? '35–39'
        : age < 45 ? '40–44' : age < 50 ? '45–49' : age < 55 ? '50–54' : age < 60 ? '55–59' : '60+';
      const key = `${band}__${c.city}`;
      if (!map[key]) map[key] = { city: c.city, band, ages: [], count: 0, names: [], contacts: [] };
      map[key].count++;
      map[key].ages.push(age);
      if (map[key].names.length < 4) map[key].names.push(`${c.first_name || ''} ${(c.last_name || '')[0] || ''}.`.trim());
      map[key].contacts.push(c);
    });
    const all = Object.values(map)
      .map(g => ({
        ...g,
        avgAge: Math.round(g.ages.reduce((s, a) => s + a, 0) / g.ages.length),
        z: g.count,
      }))
      .filter(g => g.count >= 3)
      .sort((a, b) => b.count - a.count);
    return { clusters: all.slice(0, 20), topClusters: all.slice(0, 5) };
  }, [contacts]);

  if (clusters.length === 0) return (
    <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>🔭</div>
      Renseignez la ville et la date de naissance pour voir les clusters générationnels
    </div>
  );

  const cities = [...new Set(clusters.map(c => c.city))];

  const scatterData = clusters.map(c => ({
    x: c.avgAge,
    y: cities.indexOf(c.city),
    z: c.count,
    label: c.count,
    city: c.city,
    band: c.band,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ResponsiveContainer width="100%" height={Math.max(220, cities.length * 28)}>
        <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
          <CartesianGrid stroke="#EEF0F8" strokeDasharray="3 3" />
          <XAxis
            type="number" dataKey="x" name="Âge moyen"
            domain={['auto', 'auto']}
            label={{ value: 'Âge moyen du cluster', position: 'insideBottom', offset: -10, fontSize: 11, fill: 'var(--muted)' }}
            tick={{ fontSize: 11 }} tickLine={false}
          />
          <YAxis
            type="number" dataKey="y" name="Ville"
            tickFormatter={(v) => cities[v] || ''}
            domain={[-0.5, cities.length - 0.5]}
            tick={{ fontSize: 11 }} tickLine={false}
            width={80}
            label={{ value: 'Clusters par ville', angle: -90, position: 'insideLeft', fontSize: 11, fill: 'var(--muted)' }}
          />
          <ZAxis type="number" dataKey="z" range={[300, 1800]} />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload;
              return (
                <div style={{ background: 'white', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 14px', fontSize: 12, boxShadow: '0 4px 16px rgba(0,0,0,.1)' }}>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>{d.band} ans — {d.city}</div>
                  <div style={{ color: VIOLET, fontWeight: 700 }}>{d.z} contacts</div>
                </div>
              );
            }}
          />
          <Scatter data={scatterData} fill={VIOLET} opacity={0.8}>
            {scatterData.map((d, i) => (
              <Cell key={i} fill={d.z >= (topClusters[0]?.count || 0) * 0.7 ? VIOLET : `${VIOLET}80`} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>

      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: 'var(--ink)' }}>Top clusters identifiés :</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {topClusters.map((c, i) => (
            <div key={i} style={{
              background: 'var(--bg)', borderRadius: 10,
              border: `2px solid ${i === 0 ? VIOLET : 'var(--line)'}`,
              overflow: 'hidden',
            }}>
              {/* Header row */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer' }}
                onClick={() => toggleCluster(i)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{c.band} ans — {c.city}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {c.names.join(', ')}{c.count > 4 ? '…' : ''}
                  </div>
                </div>
                <div style={{
                  background: VIOLET, color: 'white', fontWeight: 900,
                  fontSize: 15, width: 38, height: 38, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>{c.count}</div>
                <span style={{ fontSize: 12, color: 'var(--muted)', flexShrink: 0 }}>
                  {expandedClusters[i] ? '▲' : '▼'}
                </span>
              </div>
              {/* Expanded contacts list */}
              {expandedClusters[i] && (
                <div style={{ borderTop: '1px solid var(--line)', padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {c.contacts.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '')).map(ct => (
                    <Link key={ct.id} to={`/contacts/${ct.id}`} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      textDecoration: 'none', padding: '5px 6px',
                      borderRadius: 7, transition: 'background .12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: `${VIOLET}22`, color: VIOLET,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800, flexShrink: 0,
                      }}>
                        {(ct.first_name || '?')[0]}{(ct.last_name || '')[0] || ''}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ct.first_name} {ct.last_name}
                        </div>
                        {ct.email && <div style={{ fontSize: 10, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.email}</div>}
                      </div>
                      {ct.category === 'VIP' && <span style={{ fontSize: 10, background: '#FEF3C7', color: '#92400E', borderRadius: 6, padding: '1px 6px', fontWeight: 700, flexShrink: 0 }}>VIP</span>}
                      <span style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>{getAge(ct.birth_date)} ans</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: `${GREEN}10`, border: `1px solid ${GREEN}40`, borderRadius: 12, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 22 }}>👥</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 13, color: GREEN }}>Opportunités événementielles</div>
            <div style={{ fontSize: 12, marginTop: 3, color: 'var(--ink)' }}>
              {clusters.filter(c => c.count >= 10).length} cluster{clusters.filter(c => c.count >= 10).length > 1 ? 's' : ''} de 10+ personnes identifié{clusters.filter(c => c.count >= 10).length > 1 ? 's' : ''}.
            </div>
            {topClusters[0] && (
              <div style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic', marginTop: 4 }}>
                💡 <strong>Conseil marketing :</strong> Organisez des événements ciblés par cluster.
                Ex : "{topClusters[0].band} ans à {topClusters[0].city}" ({topClusters[0].count} personnes).
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* TAB 2 — Clients & Segments                      */
/* ─────────────────────────────────────────────── */
function TabSegments({ analytics, segments, vip, contacts, tenant, aiInsights }) {
  const total = analytics.total_contacts || 1;
  const hasAiCredits = (tenant?.ai_used ?? 0) < (tenant?.ai_limit ?? Infinity);

  /* ── Personas from real contact data — same algorithm as TabOverview ── */
  const personas = useMemo(() => buildPersonas(contacts).map(p => ({
    ...p,
    avgAge: p.averageAge,
    pct: p.percentage,
    city: p.location,
  })), [contacts]);

  /* ── Familles ── */
  const rawFamilies = analytics.category_distribution ?? [];
  const families = rawFamilies.length > 0
    ? rawFamilies.slice(0, 6).map(cat => ({ label: cat.name, value: cat.count, percent: Math.round(cat.count / total * 100) }))
    : (analytics.total_contacts > 0 ? [{ label: 'Non catégorisé', value: analytics.total_contacts, percent: 100 }] : []);

  /* ── Relationship states ── */
  const active  = analytics.active_contacts  || 0;
  const dormant = analytics.dormant_contacts || 0;
  const silent  = Math.max(0, total - active - dormant);
  const relStates = [
    { label: 'Actifs',     value: active,  pct: Math.round(active  / total * 100), icon: '✅', color: GREEN  },
    { label: 'Silencieux', value: silent,  pct: Math.round(silent  / total * 100), icon: '🔇', color: YELLOW },
    { label: 'À risque',   value: dormant, pct: Math.round(dormant / total * 100), icon: '⚠️', color: RED    },
  ];

  /* ── Geography ── */
  const geoCities = (analytics.geo_distribution ?? []).slice(0, 8);

  /* ── Qualification ── */
  const cnt = (contacts || []).length || 1;
  const cityRate  = Math.round((contacts || []).filter(c => c.city).length     / cnt * 100);
  const catRate   = Math.round((contacts || []).filter(c => c.category).length / cnt * 100);
  const notesRate = Math.round((contacts || []).filter(c => c.notes).length    / cnt * 100);
  const civRate   = analytics.civilite_rate ?? 0;
  const qualBars  = [
    { label: 'Civilité renseignée',  value: civRate   },
    { label: 'Ville renseignée',     value: cityRate  },
    { label: 'Catégorie client',     value: catRate   },
    { label: 'Notes / préférences',  value: notesRate },
  ];
  const qualScore = Math.round(qualBars.reduce((s, b) => s + b.value, 0) / qualBars.length);

  /* ── Recommended actions ── */
  const actions = [
    { segment: 'VIP',               icon: '💎', color: YELLOW, bg: '#FFFBEB', meaning: 'Clients importants',           action: 'Attention personnalisée et message premium' },
    { segment: 'Clients silencieux', icon: '🔇', color: CYAN,  bg: '#ECFEFF', meaning: 'Peu d\'interactions récentes',  action: 'Réactivation douce et personnalisée' },
    { segment: 'Prospects chauds',  icon: '🔥', color: PINK,   bg: '#FFF0F7', meaning: 'Intérêt démontré',              action: 'Proposition ciblée ou rendez-vous' },
    { segment: 'Anciens clients',   icon: '💤', color: VIOLET, bg: '#EDE9FF', meaning: 'Relation dormante',             action: 'Message "on pense à vous"' },
  ];

  /* ── Banner text ── */
  const top = personas[0];
  const bannerText = top
    ? `Votre clientèle est dominée par ${top.count} ${top.gender === 'femme' ? 'femmes' : top.gender === 'homme' ? 'hommes' : 'contacts'} (${top.pct}%) — âge moyen ${top.avgAge} ans.${dormant > 0 ? ` ${dormant} contact${dormant > 1 ? 's' : ''} dormant${dormant > 1 ? 's' : ''} à réactiver.` : ' Base active et engagée !'}`
    : `Vous avez ${total} contact${total > 1 ? 's' : ''} dans votre base.${dormant > 0 ? ` ${dormant} dormant${dormant > 1 ? 's' : ''} à réactiver !` : ' Continuez à enrichir vos fiches.'}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {!hasAiCredits && <AiCreditBanner used={tenant?.ai_used} limit={tenant?.ai_limit} />}

      {/* ── Row 1 : Profil & Genre (fusionné) | Pyramide des âges ── */}
      <div className="overview-grid">

        {/* MERGED: Répartition par genre + Portrait des clients */}
        <div className="ov-card">
          <div className="ov-card-title">⚧ Profil de clientèle</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Donut genre — colonne gauche */}
            <div style={{ flex: '0 0 178px', minWidth: 160 }}>
              <GenderPieChart contacts={contacts} />
            </div>
            {/* Personas — colonne droite */}
            <div style={{ flex: 1, minWidth: 200 }}>
              {personas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--muted)', fontSize: 13 }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>🚻</div>Aucune donnée disponible
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {personas.map((p, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: 10, alignItems: 'center',
                      background: 'var(--bg)', borderRadius: 12,
                      padding: '10px 12px', border: '1px solid var(--line)',
                    }}>
                      <PersonaAvatar gender={p.gender} age={p.avgAge} size={48} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', marginBottom: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: VIOLET, textTransform: 'uppercase', letterSpacing: '.04em' }}>#{i + 1}</span>
                          <span style={{
                            display: 'inline-block',
                            background: p.gender === 'femme' ? '#FCE7F3' : p.gender === 'homme' ? '#EFF6FF' : '#F3F4F6',
                            color: p.gender === 'femme' ? '#BE185D' : p.gender === 'homme' ? '#1D4ED8' : '#4B5563',
                            border: `1px solid ${p.gender === 'femme' ? '#F9A8D4' : p.gender === 'homme' ? '#BFDBFE' : '#E5E7EB'}`,
                            borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                          }}>
                            {p.gender === 'femme' ? 'Femme' : p.gender === 'homme' ? 'Homme' : 'Mixte'}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700 }}>{p.ageLabel} ans</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 5 }}>
                          {p.count} contacts{p.city ? ` · ${p.city}` : ''}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, height: 4, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${p.pct}%`, background: p.gender === 'femme' ? PINK : p.gender === 'homme' ? VIOLET : CYAN, borderRadius: 3, transition: 'width .6s' }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 800, color: VIOLET }}>{p.pct}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <AiInsightBlock
            insights={aiInsights}
            cardKey="portrait"
            fallbackAnalyse={personas.length > 0 ? `Profil dominant : ${personas[0].gender === 'femme' ? 'femmes' : personas[0].gender === 'homme' ? 'hommes' : 'autres'} (~${personas[0].avgAge} ans, ${personas[0].pct}% de la base).` : `Enrichissez vos fiches avec la civilité pour voir la répartition.`}
            fallbackConseil={`Adaptez le ton de vos messages selon le genre dominant — un message personnalisé augmente le taux d'ouverture de 20–30%.`}
            fallbackLink="/contacts"
            fallbackLinkLabel="Analyser ma clientèle"
          />
        </div>

        <div className="ov-card">
          <div className="ov-card-title">👥 Pyramide des âges</div>
          <AgePyramidChart contacts={contacts} />
          <AiInsightBlock
            insights={aiInsights}
            cardKey="sanite"
            fallbackAnalyse={analytics.total_contacts > 0 ? `Base de ${analytics.total_contacts} contacts — analysez la pyramide pour adapter vos campagnes par génération.` : `Importez vos contacts pour voir la pyramide des âges de votre clientèle.`}
            fallbackConseil={analytics.total_contacts > 0 ? `Segmentez vos campagnes par tranche d'âge pour personnaliser vos messages et améliorer les taux d'ouverture.` : `Ajoutez des contacts avec leur date de naissance pour débloquer l'analyse générationnelle.`}
            fallbackLink={analytics.total_contacts > 0 ? "/campaigns/new" : "/contacts"}
            fallbackLinkLabel={analytics.total_contacts > 0 ? "Créer une campagne ciblée" : "Importer des contacts"}
          />
        </div>
      </div>

      {/* ── Row 2 : Familles | Géographie ── */}
      <div className="overview-grid">
        <div className="ov-card">
          <div className="ov-card-title">🫧 Familles de clients</div>
          {families.length === 0 ? (
            <div style={{ height: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--muted)', fontSize: 13 }}>
              <span style={{ fontSize: 28 }}>👥</span>
              <span>Ajoutez des contacts pour voir vos segments</span>
            </div>
          ) : <>
          <BubbleField data={families} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {families.map((f, i) => (
              <div key={f.label} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'var(--bg)', border: '1px solid var(--line)',
                borderRadius: 20, padding: '3px 9px',
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)' }}>{f.label} ({f.percent}%)</span>
              </div>
            ))}
          </div>
          {families.length > 0 && (
            <AiInsightBlock
              insights={aiInsights}
              cardKey="qualification"
              fallbackAnalyse={`Catégorie dominante : "${families[0].label}" avec ${families[0].value} contacts (${families[0].percent}%).`}
              fallbackConseil="Créez des campagnes distinctes par famille — un message ciblé est 3× plus efficace qu'un envoi générique."
              fallbackLink="/contacts"
              fallbackLinkLabel="Gérer mes segments"
            />
          )}
          </>}
        </div>

        <div className="ov-card">
          <div className="ov-card-title">📍 Répartition géographique</div>
          {geoCities.length > 0 ? (
            <>
              <SwissMap cities={geoCities} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {geoCities.slice(0, 6).map((c, i) => {
                  const pct = Math.round(c.count / total * 100);
                  return (
                    <div key={c.city} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 800, fontSize: 11, color: 'var(--muted)', width: 16, textAlign: 'right' }}>{i + 1}</span>
                      <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{c.city}</span>
                      <div style={{ width: 90, height: 5, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(pct * 6, 100)}%`, background: COLORS[i % COLORS.length], borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--muted)', width: 56, textAlign: 'right' }}>{c.count} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)', fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📍</div>Aucune donnée géographique disponible
            </div>
          )}
          <AiInsightBlock
            insights={aiInsights}
            cardKey="geo"
            fallbackAnalyse={geoCities[0] ? `Ville principale : ${geoCities[0].city} (${Math.round(geoCities[0].count / total * 100)}% de la base).` : `Enrichissez les fiches contacts avec les villes pour débloquer la géo-segmentation.`}
            fallbackConseil={geoCities.length > 1 ? `Créez des campagnes géolocalisées par zone — un message local augmente l'engagement de 35%.` : `Renseignez les villes dans vos fiches contacts pour personnaliser vos envois par région.`}
            fallbackLink="/contacts"
            fallbackLinkLabel="Enrichir les villes"
          />
        </div>
      </div>

      {/* ── Full width : Clusters Générationnels ── */}
      <div className="ov-card" style={{ gridColumn: '1 / -1' }}>
        <div className="ov-card-title">🔭 Clusters Générationnels</div>
        <GenerationalClusters contacts={contacts} />
      </div>

    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* TAB 3 — Performance Marketing                   */
/* ─────────────────────────────────────────────── */
function TabMarketing({ analytics, contacts, tenant, aiInsights }) {
  const hasAiCredits = (tenant?.ai_used ?? 0) < (tenant?.ai_limit ?? Infinity);

  /* ── Template perf data — depuis les vraies données analytics ── */
  const rawTemplatePerf = analytics.template_performance ?? [];
  // Fallback RÉEL : si le backend ne remonte rien, on reconstruit la perf par
  // type depuis les logs e-mail + SMS (anniversaire vs campagne). Zéro fictif.
  const [fbPerf, setFbPerf] = useState([]);
  useEffect(() => {
    if (rawTemplatePerf.length > 0) return;
    Promise.all([
      api.getEmailLogs().catch(() => null),
      api.getSmsLogs().catch(() => null),
    ]).then(([em, sm]) => {
      const elogs = Array.isArray(em?.logs) ? em.logs : (Array.isArray(em) ? em : []);
      const slogs = Array.isArray(sm?.logs) ? sm.logs : (Array.isArray(sm) ? sm : []);
      const out = [];
      const push = (name, list, openedFn) => {
        if (list.length > 0) out.push({ name, sent: list.length, opened: list.filter(openedFn).length });
      };
      push('🎂 E-mail anniv',    elogs.filter(l => !l.campaign_id), l => l.first_opened_at);
      push('📣 E-mail campagne', elogs.filter(l => l.campaign_id),  l => l.first_opened_at);
      push('🎂 SMS anniv',       slogs.filter(l => getSmsType(l).id === 'birthday' && l.status !== 'failed'), l => ['delivered', 'read'].includes(l.status));
      push('📱 SMS campagne',    slogs.filter(l => getSmsType(l).id !== 'birthday' && l.status !== 'failed'), l => ['delivered', 'read'].includes(l.status));
      setFbPerf(out);
    }).catch(() => {});
  }, [rawTemplatePerf.length]);

  const perfSource = rawTemplatePerf.length > 0 ? rawTemplatePerf : fbPerf;
  const templatePerf = perfSource.length > 0
    ? perfSource.map((t, i) => ({
        name:   t.name   ?? t.type  ?? `Template ${i+1}`,
        sent:   t.sent   ?? t.sent_count   ?? 0,
        opened: t.opened ?? t.opened_count ?? 0,
        rate:   t.rate   ?? (t.sent > 0 ? Math.round(t.opened / t.sent * 100) : 0),
        color:  [YELLOW, GREEN, VIOLET, PINK, CYAN][i % 5],
      }))
    : [];

  const hasEmailData = (analytics.emails_sent_month ?? 0) > 0 || templatePerf.length > 0;

  // Radar basé sur les vraies données si disponibles, sinon vide
  const radarData = analytics.coverage_radar ?? (hasEmailData ? [
    { subject: 'Anniversaires', A: analytics.birthday_rate   ?? 0 },
    { subject: 'Fidélisation',  A: analytics.loyalty_rate    ?? 0 },
    { subject: 'Réactivation',  A: analytics.reactivation_rate ?? 0 },
    { subject: 'Promotions',    A: analytics.promo_rate      ?? 0 },
    { subject: 'Personnalisé',  A: analytics.custom_rate     ?? 0 },
    { subject: 'Collecte data', A: analytics.email_rate      ?? 0 },
  ] : []);

  const bestTemplate = templatePerf.length > 0
    ? templatePerf.reduce((max, t) => t.rate > max.rate ? t : max, templatePerf[0])
    : null;

  /* ── Funnel data from real analytics ── */
  const activeContacts = analytics.active_contacts || 0;
  const withEmail      = Math.round(activeContacts * (analytics.email_rate || 0) / 100);
  const sentMonth      = analytics.emails_sent_month || 0;
  const openedMonth    = Math.round(sentMonth * (analytics.open_rate_month || 0) / 100);

  const funnelSteps = [
    { label: 'Contacts actifs',       value: activeContacts, pct: 100,  color: VIOLET },
    { label: 'Avec email',            value: withEmail,      pct: activeContacts > 0 ? Math.round(withEmail / activeContacts * 100) : 0, color: VIOLET },
    { label: 'Emails envoyés / mois', value: sentMonth,      pct: withEmail > 0 ? Math.round(sentMonth / withEmail * 100) : 0, color: VIOLET },
    { label: 'Emails ouverts',        value: openedMonth,    pct: sentMonth > 0 ? analytics.open_rate_month : 0, color: GREEN },
  ];

  const marketingBannerText = aiInsights === null
    ? 'Analyse en cours…'
    : (aiInsights?.marketing_summary ?? (hasEmailData && bestTemplate
        ? `Taux d'ouverture de ${analytics.open_rate_month}% ce mois — ${analytics.open_rate_month >= 35 ? 'au-dessus' : 'en-dessous'} de la moyenne secteur (35%). Le template "${bestTemplate.name}" surperforme à ${bestTemplate.rate}%.`
        : `Configurez votre SMTP et envoyez vos premiers e-mails pour débloquer les analyses marketing.`));

  const avgTemplateRate = templatePerf.length > 0
    ? Math.round(templatePerf.reduce((s, t) => s + t.rate, 0) / templatePerf.length)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {!hasAiCredits && <AiCreditBanner used={tenant?.ai_used} limit={tenant?.ai_limit} />}

      {/* ── KPIs ── */}
      <div className="analytics-kpi-grid">
        <StatCard label="E-mails ce mois"  value={analytics.emails_sent_month}       icon="📤" color="violet" />
        <StatCard label="Taux d'ouverture" value={`${analytics.open_rate_month}%`}   icon="👁"  color="green"
          sub={analytics.open_rate_month >= 35 ? '✅ Au-dessus de la moyenne (35%)' : analytics.open_rate_month > 0 ? '⚠️ Sous la moyenne (35%)' : 'Secteur moy. 35%'} />
        <StatCard label="Feedbacks reçus"  value={analytics.feedback_month}           icon="💛" color="yellow" sub="Ce mois" />
        <StatCard label="Score engagement" value={`${analytics.avg_engagement}/100`}  icon="⚡" color="pink"
          sub={analytics.avg_engagement >= 60 ? '🔥 Base très engagée' : analytics.avg_engagement >= 35 ? '📈 Engagement moyen' : analytics.avg_engagement > 0 ? '💡 À stimuler' : 'Moyenne contacts'} />
      </div>

      {/* ── Insight rapide ── */}
      {hasEmailData && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {[
            analytics.open_rate_month >= 40 && { icon: '🏆', color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', text: `Taux d'ouverture exceptionnel — top 10 % du secteur` },
            analytics.open_rate_month > 0 && analytics.open_rate_month < 20 && { icon: '💡', color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A', text: `Objet plus personnalisé + envoi mardi-jeudi matin → +15 % d'ouvertures estimé` },
            bestTemplate && { icon: '⭐', color: '#5A35FF', bg: '#F5F3FF', border: '#DDD6FE', text: `Meilleur template : "${bestTemplate.name}" à ${bestTemplate.rate}% d'ouverture` },
            analytics.feedback_month > 0 && { icon: '💛', color: '#DB2777', bg: '#FDF2F8', border: '#F9A8D4', text: `${analytics.feedback_month} client${analytics.feedback_month > 1 ? 's ont' : ' a'} répondu positivement ce mois` },
          ].filter(Boolean).slice(0, 3).map((item, i) => (
            <div key={i} style={{ background: item.bg, border: `1px solid ${item.border}`, borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: item.color, lineHeight: 1.4 }}>{item.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Funnel de conversion ── */}
      <div>

        {/* Funnel */}
        <div className="ov-card">
          <div className="ov-card-title">▼ Funnel de conversion</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0 4px' }}>
            {funnelSteps.map((step, i) => (
              <div key={step.label} style={{
                background: i % 2 === 0 ? `${VIOLET}0D` : `${VIOLET}07`,
                border: `1px solid ${VIOLET}18`,
                borderRadius: 10,
                padding: '13px 18px',
                display: 'flex', alignItems: 'center', gap: 12,
                marginLeft: `${i * 14}px`,
                transition: 'margin .3s',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: VIOLET }}>{step.value.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{step.label}</div>
                </div>
                <div style={{
                  fontSize: 13, fontWeight: 800,
                  color: step.pct >= 80 ? GREEN : step.pct >= 40 ? YELLOW : RED,
                }}>{step.pct}%</div>
              </div>
            ))}
          </div>
          <AiInsightBlock
            insights={aiInsights}
            cardKey="funnel"
            fallbackAnalyse={funnelSteps[1].pct >= 90
              ? `Bonne couverture email (${funnelSteps[1].pct}%). Taux d'activation : ${funnelSteps[2].pct}% des contacts avec email reçoivent un envoi ce mois.`
              : `${100 - funnelSteps[1].pct}% de contacts actifs sans email — chaque adresse collectée augmente directement votre portée.`}
            fallbackConseil={funnelSteps[3].pct >= 35
              ? `Taux d'ouverture excellent à ${funnelSteps[3].pct}% ! Ajoutez un CTA clair dans vos messages pour convertir ces ouvertures en actions.`
              : `Testez des objets personnalisés avec le prénom — +25% d'ouverture en moyenne. Ciblez d'abord vos VIP.`}
            fallbackLink="/contacts"
            fallbackLinkLabel="Améliorer ma base"
          />
        </div>

      </div>

      {/* ── Couverture marketing (radar) ── */}
      <div className="overview-grid">
        <div className="ov-card">
          <div className="ov-card-title">📊 Performance par type de message</div>
          {templatePerf.length > 0 ? (
            <div className="an-chart-area" style={{ margin: '0 14px' }}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={templatePerf} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F8" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sent"   name="Envoyés" fill={VIOLET} radius={[4,4,0,0]} maxBarSize={28} />
                <Bar dataKey="opened" name="Ouverts"  fill={YELLOW} radius={[4,4,0,0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--muted)', fontSize: 13 }}>
              <span style={{ fontSize: 32 }}>📭</span>
              <span>Aucun envoi pour l'instant</span>
              <span style={{ fontSize: 11 }}>Les stats par template apparaîtront ici après vos premiers envois.</span>
            </div>
          )}
          {bestTemplate && (
            <AiInsightBlock
              insights={aiInsights}
              cardKey="portrait"
              fallbackAnalyse={`"${bestTemplate.name}" domine avec ${bestTemplate.rate}% — ${bestTemplate.rate - avgTemplateRate} pts au-dessus de la moyenne (${avgTemplateRate}%).`}
              fallbackConseil={`Doublez vos envois anniversaires avec une offre exclusive — c'est votre canal le plus rentable.`}
              fallbackLink="/templates"
              fallbackLinkLabel="Optimiser mes templates"
            />
          )}
        </div>
        <div className="ov-card">
          <div className="ov-card-title">🎯 Couverture marketing</div>
          {radarData.length > 0 ? (
            <div className="an-chart-area" style={{ margin: '0 14px' }}>
            <ResponsiveContainer width="100%" height={200}>
              <RadarChart data={radarData} margin={{ top: 10, right: 32, bottom: 10, left: 32 }}>
                <PolarGrid stroke="#D4D9FF" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} tickLine={false} />
                <Radar dataKey="A" stroke={VIOLET} fill={VIOLET} fillOpacity={0.25} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </RadarChart>
            </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--muted)', fontSize: 13 }}>
              <span style={{ fontSize: 32 }}>🎯</span>
              <span>Données insuffisantes</span>
              <span style={{ fontSize: 11 }}>Envoyez vos premières campagnes pour mesurer votre couverture.</span>
            </div>
          )}
          {hasEmailData && (
            <AiInsightBlock
              insights={aiInsights}
              cardKey="sanite"
              fallbackAnalyse={`Anniversaires : votre point fort. Réactivation et messages personnalisés à développer.`}
              fallbackConseil={`2 campagnes de réactivation par trimestre → +CHF ${Math.round((analytics.dormant_contacts || 0) * 2)} de potentiel récupéré.`}
              fallbackLink="/campaigns/new"
              fallbackLinkLabel="Lancer une campagne"
            />
          )}
        </div>
      </div>

    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* TAB 4 — Emails & Tracking                       */
/* ─────────────────────────────────────────────── */
// ═══════════════════════════════════════════════════════════════
// TAB SMS
// ═══════════════════════════════════════════════════════════════
// ── Détection du type de SMS depuis le contenu / champ type ──────────────────
function getSmsType(log) {
  const msg  = (log.message || '').toLowerCase();
  const type = (log.type   || '').toLowerCase();
  if (type === 'birthday'  || msg.includes('anniversaire'))                        return { id: 'birthday',     icon: '🎂', label: 'Anniversaire', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D' };
  if (type === 'reminder'  || msg.includes('rappel'))                              return { id: 'reminder',     icon: '🔔', label: 'Rappel',        color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA' };
  if (type === 'alert'     || msg.includes('alerte') || msg.includes('alert'))     return { id: 'alert',        icon: '⚠️', label: 'Alerte',        color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5' };
  if (type === 'reactivation' || msg.includes('manqu') || msg.includes('revenez')) return { id: 'reactivation', icon: '💤', label: 'Réactivation',  color: '#0369A1', bg: '#E0F2FE', border: '#7DD3FC' };
  if (type === 'promo'     || msg.includes('offre') || msg.includes('promo'))      return { id: 'promo',        icon: '🎯', label: 'Promotion',     color: '#10B981', bg: '#D1FAE5', border: '#6EE7B7' };
  return                                                                            { id: 'campaign',    icon: '📤', label: 'Campagne',      color: '#6B7280', bg: '#F3F4F6', border: '#D1D5DB' };
}

function TabSms({ tenant }) {
  const { user } = useApp();
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  // Historique : les anniversaires restent ligne par ligne (jour J), le reste
  // est REGROUPÉ par type + message (sinon listes énormes pour une campagne).
  const [openGroups, setOpenGroups] = useState({});
  const [histPage, setHistPage] = useState(0);
  const [histSize, setHistSize] = useState(50);
  const [openDays, setOpenDays] = useState(() => new Set());
  const displayRows = useMemo(() => {
    const rows = []; const groups = {};
    logs.forEach(log => {
      const t = getSmsType(log);
      const d = log.sent_at ? new Date(String(log.sent_at).replace(' ', 'T')) : null;
      if (t.id === 'birthday') { rows.push({ kind: 'single', log, date: d }); return; }
      const key = `${t.id}|${log.message || ''}`;
      if (!groups[key]) { groups[key] = { kind: 'group', key, type: t, logs: [], date: d }; rows.push(groups[key]); }
      groups[key].logs.push(log);
      if (d && (!groups[key].date || d > groups[key].date)) groups[key].date = d;
    });
    rows.sort((a, b) => (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0));
    return rows;
  }, [logs]);

  useEffect(() => {
    api.getSmsLogs()
      .then(data => setLogs(Array.isArray(data) ? data : []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, []);

  // Stats — TOUS les types décomptent du forfait
  const sentLogs   = logs.filter(l => l.status !== 'failed');
  const sent       = sentLogs.length;                                                          // total envoyés réussis
  const failed     = logs.filter(l => l.status === 'failed').length;
  // Détail par type — uniquement les envois réussis
  const birthday   = sentLogs.filter(l => getSmsType(l).id === 'birthday').length;
  const reminders  = sentLogs.filter(l => getSmsType(l).id === 'reminder').length;
  const campaigns  = sentLogs.filter(l => getSmsType(l).id === 'campaign').length;
  const alerts     = sentLogs.filter(l => getSmsType(l).id === 'alert').length;
  const credits      = tenant?.sms_credits ?? 0;
  const creditsLimit = tenant?.sms_limit ?? 500;
  const usedCredits  = creditsLimit - credits;
  // Nouveaux indicateurs
  const deliveryRate = logs.length === 0 ? 100 : Math.round((sent / logs.length) * 100);
  const parseDate = (str) => { if (!str) return null; const d = new Date(String(str).replace(' ', 'T')); return isNaN(d.getTime()) ? null : d; };
  const lastLogDate = logs.length > 0
    ? logs.reduce((latest, l) => { const d = parseDate(l.sent_at || l.created_at); return d && (!latest || d > latest) ? d : latest; }, null)
    : null;
  const daysSinceLast = lastLogDate
    ? Math.floor((Date.now() - lastLogDate.getTime()) / 86400000)
    : null;

  const STATUS_COLORS = {
    sent:      { bg: '#F0FDF4', border: '#86EFAC', color: '#166534', label: '✅ Envoyé' },
    failed:    { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B', label: '❌ Échoué' },
    delivered: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8', label: '📬 Livré' },
    read:      { bg: '#F5F3FF', border: '#DDD6FE', color: '#6D28D9', label: '👁️ Lu' },
  };

  // Répartition par type pour la mini-légende
  const typeBreakdown = logs.reduce((acc, log) => {
    const t = getSmsType(log);
    acc[t.id] = (acc[t.id] || { ...t, count: 0 });
    acc[t.id].count++;
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 8 }}>

      {/* ── Conseil génie ── */}
      <div className="an-conseil-banner">
        <img src={mascotImg} alt="Appee" className="an-conseil-mascot" />
        <div className="an-conseil-body">
          <div className="an-conseil-title">
            {credits === 0
              ? 'Plus de crédits SMS !'
              : credits < 10
                ? 'Tes crédits SMS sont presque épuisés'
                : birthday > 0
                  ? `${birthday} SMS d'anniversaire envoyés automatiquement 🎂`
                  : 'Le SMS, le canal le plus lu'}
          </div>
          <div className="an-conseil-text">
            {credits === 0
              ? 'Aucun SMS ne peut partir. Passe au plan supérieur pour débloquer de nouveaux crédits et ne plus rater aucun anniversaire.'
              : credits < 10
                ? `Il ne reste que ${credits} crédit${credits > 1 ? 's' : ''}. Anniversaires, rappels et campagnes consomment chacun 1 crédit.`
                : birthday > 0
                  ? `Appee a envoyé ${birthday} SMS d'anniversaire automatiquement${reminders > 0 ? ` + ${reminders} rappel${reminders > 1 ? 's' : ''}` : ''}. Taux de lecture 98% — tes clients les voient tous.`
                  : 'Les SMS ont un taux d\'ouverture de 98% contre 22% pour les e-mails. Chaque envoi (anniversaire, rappel, campagne) consomme 1 crédit.'}
          </div>
        </div>
        <Link to="/settings" className="an-conseil-btn">
          {credits === 0 ? 'Changer de plan →' : credits < 10 ? 'Gérer les crédits →' : 'Créer une campagne SMS →'}
        </Link>
      </div>

      {/* ── Insights contextuels SMS ── */}
      {sent > 0 && (() => {
        const insights = [];
        if (deliveryRate >= 95)
          insights.push({ icon: '📡', color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7',
            title: `Excellent taux de livraison — ${deliveryRate}%`,
            sub: `${sent} SMS livrés sur ${logs.length} envoyés. Tes contacts reçoivent bien tes messages.`,
            cta: null });
        else if (failed > 0)
          insights.push({ icon: '⚠️', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D',
            title: `${failed} SMS non délivrés`,
            sub: `Vérifie les numéros de téléphone dans tes fiches contacts — un numéro incorrect bloque la livraison.`,
            cta: '/contacts' });
        if (birthday > 0)
          insights.push({ icon: '🎂', color: '#DB2777', bg: '#FDF2F8', border: '#F9A8D4',
            title: `${birthday} SMS d'anniversaire envoyés en automatique`,
            sub: `Les SMS anniversaires ont un taux de lecture de 98% — Appee gère tout sans aucune action de ta part.`,
            cta: '/birthdays' });
        if (credits > 0 && usedCredits > 0) {
          const daysUsed = daysSinceLast !== null ? Math.max(daysSinceLast, 1) : 30;
          const avgPerDay = usedCredits / Math.min(30, daysUsed);
          const daysLeft = avgPerDay > 0 ? Math.round(credits / avgPerDay) : null;
          if (daysLeft !== null && daysLeft < 30)
            insights.push({ icon: '💳', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D',
              title: `Crédits estimés pour ~${daysLeft} jours`,
              sub: `À ce rythme (${avgPerDay.toFixed(1)} SMS/jour), tes ${credits} crédits restants couvrent environ ${daysLeft} jours.`,
              cta: '/settings/plan' });
        }
        if (insights.length === 0) return null;
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${insights.length}, 1fr)`, gap: 12 }}>
            {insights.map((ins, i) => (
              <div key={i} style={{
                background: ins.bg, border: `1.5px solid ${ins.border}`,
                borderRadius: 14, padding: '16px 18px',
                display: 'flex', gap: 14, alignItems: 'flex-start',
              }}>
                <div style={{ fontSize: 28, flexShrink: 0, lineHeight: 1, marginTop: 2 }}>{ins.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: ins.color, marginBottom: 5, lineHeight: 1.3 }}>{ins.title}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.55 }}>{ins.sub}</div>
                  {ins.cta && (
                    <Link to={ins.cta} style={{ display: 'inline-block', marginTop: 9, fontSize: 12, fontWeight: 700, color: ins.color, textDecoration: 'none' }}>
                      Voir →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Cartes stats ── */}
      {(() => {
        const detailCards = [
          birthday  > 0 && { label: 'Anniversaires',   value: birthday,  icon: '🎂', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', sub: 'Auto Appee' },
          reminders > 0 && { label: 'Rappels',          value: reminders, icon: '🔔', color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA', sub: 'Depuis Rappels' },
          alerts    > 0 && { label: 'Alertes',          value: alerts,    icon: '⚠️', color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5', sub: 'Depuis Alertes' },
          campaigns > 0 && { label: 'Campagnes',        value: campaigns, icon: '📤', color: '#6B7280', bg: '#F3F4F6', border: '#D1D5DB', sub: 'Manuel' },
          { label: 'Taux de livraison', value: `${deliveryRate}%`, icon: '📊',
            color: deliveryRate >= 95 ? '#10B981' : deliveryRate >= 80 ? '#F59E0B' : '#EF4444',
            bg:    deliveryRate >= 95 ? '#ECFDF5' : deliveryRate >= 80 ? '#FFFBEB' : '#FEF2F2',
            border:deliveryRate >= 95 ? '#6EE7B7' : deliveryRate >= 80 ? '#FCD34D' : '#FCA5A5',
            sub: `${sent} envoyés · ${failed} échec${failed > 1 ? 's' : ''}` },
          { label: 'Dernier envoi', value: daysSinceLast === null ? '—' : daysSinceLast === 0 ? 'Auj.' : `${daysSinceLast}j`,
            icon: '📅', color: '#0369A1', bg: '#E0F2FE', border: '#7DD3FC',
            sub: lastLogDate ? lastLogDate.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }) : 'Aucun envoi' },
          { label: 'Crédits restants', value: credits, icon: '💳',
            color: credits === 0 ? '#EF4444' : credits < 20 ? '#F59E0B' : '#10B981',
            bg:    credits === 0 ? '#FEF2F2' : credits < 20 ? '#FFFBEB' : '#ECFDF5',
            border:credits === 0 ? '#FCA5A5' : credits < 20 ? '#FCD34D' : '#6EE7B7',
            sub: `${usedCredits} utilisés / ${creditsLimit}` },
        ].filter(Boolean);
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `200px repeat(${detailCards.length}, 1fr)`, gap: 10, alignItems: 'stretch' }}>
            {/* Carte principale — total */}
            <div style={{
              background: 'linear-gradient(135deg,#10B981,#059669)',
              borderRadius: 16, padding: '20px 24px',
              color: '#fff', boxShadow: '0 4px 16px rgba(16,185,129,.3)',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, opacity: .85, marginBottom: 4 }}>Total SMS envoyés</div>
              <div style={{ fontSize: 44, fontWeight: 900, lineHeight: 1 }}>{sent}</div>
              <div style={{ fontSize: 11, opacity: .8, marginTop: 6 }}>
                {failed > 0 && <span style={{ background: 'rgba(255,255,255,.2)', borderRadius: 999, padding: '2px 7px', marginRight: 6 }}>❌ {failed} échec{failed > 1 ? 's' : ''}</span>}
                Tous types · 30j
              </div>
            </div>
            {/* Cartes détail — grille égale */}
            {detailCards.map(({ label, value, icon, color, bg, border, sub }) => (
              <div key={label} style={{
                background: bg, border: `1.5px solid ${border}`,
                borderRadius: 14, padding: '14px 16px',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              }}>
                <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color, lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1F2937', marginTop: 4 }}>{label}</div>
                <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>{sub}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Jauge crédits + répartition par type ── */}
      <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 14, padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>💳 Crédits SMS — {credits} restants sur {creditsLimit}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.values(typeBreakdown).map(t => (
              <span key={t.id} style={{ fontSize: 11, fontWeight: 700, background: t.bg, color: t.color, border: `1px solid ${t.border}`, borderRadius: 999, padding: '2px 8px' }}>
                {t.icon} {t.label} {t.count}
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 10, background: '#E5E7EB', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 5,
              width: `${Math.min(100, Math.round((credits / (creditsLimit || 1)) * 100))}%`,
              background: credits === 0 ? '#EF4444' : credits < 20 ? '#F59E0B' : '#10B981',
              transition: 'width 0.4s',
            }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: credits === 0 ? '#EF4444' : 'var(--text)', minWidth: 60, textAlign: 'right' }}>
            {credits} / {creditsLimit}
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
          {credits === 0
            ? '🔴 Plus de crédits — passez au plan supérieur pour continuer les envois.'
            : credits < 20
              ? `⚠️ Bientôt épuisés — ${credits} crédit${credits > 1 ? 's' : ''} restant${credits > 1 ? 's' : ''}. Anniversaires, rappels et campagnes consomment chacun 1 crédit.`
              : `Chaque SMS envoyé (anniversaire, rappel, alerte, campagne) consomme 1 crédit du forfait.`}
        </div>
      </div>

      {/* ── Tableau historique ── */}
      <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 14, padding: '18px 20px' }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>📋 Historique des envois SMS</div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', fontSize: 13 }}>Chargement…</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', fontSize: 13 }}>Aucun SMS envoyé pour l'instant.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--line)' }}>
                  {['Contact', 'Téléphone', 'Type', 'Message', 'Statut', 'Date'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const renderRow = (log, indent = false) => {
                    const st  = STATUS_COLORS[log.status] || STATUS_COLORS.sent;
                    const smsType = getSmsType(log);
                    return (
                    <tr key={log.id} style={{ borderBottom: '1px solid var(--line)', background: indent ? 'var(--violet-soft, #F5F3FF)' : 'transparent' }}>
                      <td style={{ padding: '10px 10px', paddingLeft: indent ? 28 : 10, fontWeight: 600 }}>
                        {log.contact?.name
                          ? log.contact.name
                          : <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: 'linear-gradient(90deg,#FFC400,#FFD966)',
                              color: '#78350F', fontWeight: 700, fontSize: 11,
                              borderRadius: 999, padding: '3px 10px',
                              whiteSpace: 'nowrap',
                            }}>
                              ✦ {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : (tenant?.company_name || 'Admin')}
                            </span>
                        }
                      </td>
                      <td style={{ padding: '10px 10px', fontFamily: 'monospace', fontSize: 12, color: 'var(--muted)' }}>
                        {log.phone}
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: 11, fontWeight: 700,
                          background: smsType.bg, color: smsType.color,
                          border: `1px solid ${smsType.border}`,
                          borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap',
                        }}>
                          {smsType.icon} {smsType.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 10px', maxWidth: 240 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 220, color: 'var(--ink)' }}>
                          {log.message}
                        </span>
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        <span style={{ fontSize: 11, background: st.bg, border: `1px solid ${st.border}`, color: st.color, borderRadius: 12, padding: '3px 9px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 10px', color: 'var(--muted)', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {log.sent_at ? new Date(log.sent_at).toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                    );
                  };

                  const renderGroupRow = (row) => {
                    const open   = !!openGroups[row.key];
                    const ok     = row.logs.filter(l => l.status !== 'failed').length;
                    const ko     = row.logs.length - ok;
                    const latest = row.date;
                    return (
                      <React.Fragment key={row.key}>
                        <tr
                          style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer', background: '#FBFAFF' }}
                          onClick={() => setOpenGroups(g => ({ ...g, [row.key]: !g[row.key] }))}
                        >
                          <td style={{ padding: '10px 10px', paddingLeft: 24, fontWeight: 800 }}>
                            {open ? '▾' : '▸'} {row.logs.length} destinataires
                          </td>
                          <td style={{ padding: '10px 10px', color: 'var(--muted-2)', fontSize: 12 }}>—</td>
                          <td style={{ padding: '10px 10px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 11, fontWeight: 700,
                              background: row.type.bg, color: row.type.color,
                              border: `1px solid ${row.type.border}`,
                              borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap',
                            }}>
                              {row.type.icon} {row.type.label} ×{row.logs.length}
                            </span>
                          </td>
                          <td style={{ padding: '10px 10px', maxWidth: 240 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 220, color: 'var(--ink)' }}>
                              {row.logs[0].message}
                            </span>
                          </td>
                          <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: 11, background: '#F0FDF4', border: '1px solid #86EFAC', color: '#166534', borderRadius: 12, padding: '3px 9px', fontWeight: 600 }}>
                              ✅ {ok}
                            </span>
                            {ko > 0 && (
                              <span style={{ fontSize: 11, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: 12, padding: '3px 9px', fontWeight: 600, marginLeft: 4 }}>
                                ❌ {ko}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 10px', color: 'var(--muted)', whiteSpace: 'nowrap', fontSize: 12 }}>
                            {latest ? latest.toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                        </tr>
                        {open && row.logs.map(l => renderRow(l, true))}
                      </React.Fragment>
                    );
                  };

                  // Pagination puis regroupement mois → jour (accordéon).
                  const start = histPage * histSize;
                  const paged = displayRows.slice(start, start + histSize);
                  const months = []; const mMap = {};
                  paged.forEach(row => {
                    const d = row.date;
                    const mKey = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'na';
                    const dKey = d ? `${mKey}-${String(d.getDate()).padStart(2, '0')}` : 'na';
                    if (!mMap[mKey]) { mMap[mKey] = { key: mKey, label: d ? d.toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' }) : 'Sans date', days: {}, order: [] }; months.push(mMap[mKey]); }
                    const M = mMap[mKey];
                    if (!M.days[dKey]) { M.days[dKey] = { key: dKey, label: d ? d.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Sans date', rows: [], count: 0 }; M.order.push(dKey); }
                    M.days[dKey].rows.push(row);
                    M.days[dKey].count += (row.kind === 'group' ? row.logs.length : 1);
                  });
                  const firstDay = months[0]?.order[0];
                  const dayOpen  = (dk) => openDays.has(dk) || (openDays.size === 0 && dk === firstDay);
                  const toggleDay = (dk) => setOpenDays(s => { const base = (s.size === 0 && firstDay) ? new Set([firstDay]) : new Set(s); base.has(dk) ? base.delete(dk) : base.add(dk); return base; });

                  const out = [];
                  months.forEach(M => {
                    out.push(
                      <tr key={`m-${M.key}`}>
                        <td colSpan={6} style={{ padding: '16px 10px 6px', fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{M.label}</td>
                      </tr>
                    );
                    M.order.forEach(dk => {
                      const day = M.days[dk]; const open = dayOpen(dk);
                      out.push(
                        <tr key={`d-${dk}`} style={{ cursor: 'pointer', background: open ? 'var(--violet-soft, #F5F3FF)' : 'transparent', borderBottom: '1px solid var(--line)' }} onClick={() => toggleDay(dk)}>
                          <td colSpan={6} style={{ padding: '10px 10px' }}>
                            <span style={{ color: 'var(--violet)', marginRight: 8 }}>{open ? '▾' : '▸'}</span>
                            <b style={{ textTransform: 'capitalize' }}>{day.label}</b>
                            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: 'var(--violet-dark, #4C3BC9)', background: 'var(--violet-pale, #EDE9FF)', borderRadius: 999, padding: '2px 10px' }}>{day.count} SMS</span>
                          </td>
                        </tr>
                      );
                      if (open) day.rows.forEach(row => {
                        if (row.kind === 'single') out.push(renderRow(row.log));
                        else if (row.logs.length === 1) out.push(renderRow(row.logs[0]));
                        else out.push(renderGroupRow(row));
                      });
                    });
                  });
                  return out;
                })()}
              </tbody>
            </table>
            {(() => {
              const total = displayRows.length;
              const start = histPage * histSize;
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
                    <span>Afficher</span>
                    {[20, 50, 100].map(nn => (
                      <button key={nn} onClick={() => { setHistSize(nn); setHistPage(0); }}
                        style={{ minWidth: 34, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                          border: `1px solid ${histSize === nn ? 'var(--violet)' : 'var(--line)'}`,
                          background: histSize === nn ? 'var(--violet)' : '#fff', color: histSize === nn ? '#fff' : 'var(--muted)' }}>{nn}</button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
                    <span>{total === 0 ? 0 : start + 1}–{Math.min(start + histSize, total)} sur {total}</span>
                    <button disabled={histPage === 0} onClick={() => setHistPage(p => Math.max(0, p - 1))}
                      style={{ minWidth: 34, padding: '5px 12px', borderRadius: 8, cursor: histPage === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, border: '1px solid var(--line)', background: '#fff', color: 'var(--ink)', opacity: histPage === 0 ? 0.4 : 1 }}>‹</button>
                    <button disabled={start + histSize >= total} onClick={() => setHistPage(p => p + 1)}
                      style={{ minWidth: 34, padding: '5px 12px', borderRadius: 8, cursor: start + histSize >= total ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, border: '1px solid var(--line)', background: '#fff', color: 'var(--ink)', opacity: start + histSize >= total ? 0.4 : 1 }}>›</button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

    </div>
  );
}

function TabEmails({ analytics, tenant, aiInsights }) {
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const hasAiCredits = (tenant?.ai_used ?? 0) < (tenant?.ai_limit ?? Infinity);

  const [devicesData, setDevicesData]         = useState(null);
  const [monthlyData, setMonthlyData]         = useState(null);
  const [trackingSummary, setTrackingSummary] = useState(null);

  /* ── Heatmap from email logs ── */
  const { heatmap, heatmapMax, bestSlot } = useMemo(() => {
    const matrix = {};
    EMAIL_HOURS.forEach(h => { matrix[h] = {}; EMAIL_DAYS.forEach(d => { matrix[h][d] = 0; }); });
    emailLogs.forEach(log => {
      if (!log.sent_at || !(log.open_count > 0)) return;
      const dt = new Date(log.sent_at);
      const h  = dt.getHours();
      const day = EMAIL_DAYS[dt.getDay()];
      if (h >= 6 && h <= 20) matrix[h][day] = (matrix[h][day] || 0) + log.open_count;
    });
    const max = Math.max(1, ...EMAIL_HOURS.flatMap(h => EMAIL_DAYS.map(d => matrix[h]?.[d] || 0)));
    let best = { h: null, d: null, val: 0 };
    EMAIL_HOURS.forEach(h => EMAIL_DAYS.forEach(d => {
      if ((matrix[h]?.[d] || 0) > best.val) best = { h, d, val: matrix[h]?.[d] };
    }));
    return { heatmap: matrix, heatmapMax: max, bestSlot: best };
  }, [emailLogs]);

  /* ── Velocity by age — données réelles depuis emailLogs ── */
  const velocityData = useMemo(() => {
    const groups = [
      { group: '18–35 ans', min: 18, max: 35 },
      { group: '36–50 ans', min: 36, max: 50 },
      { group: '50+ ans',   min: 51, max: 120 },
    ];
    const now = Date.now();
    return groups.map(g => {
      const logs = emailLogs.filter(l => {
        if (!l.contact_birth_date && !l.birth_date) return false;
        const age = Math.floor((now - new Date(l.contact_birth_date ?? l.birth_date)) / 31557600000);
        return age >= g.min && age <= g.max;
      });
      if (logs.length === 0) return null;
      const opened = logs.filter(l => l.opened_at || l.status === 'opened');
      const fast = opened.filter(l => {
        if (!l.opened_at || !l.sent_at) return false;
        return (new Date(l.opened_at) - new Date(l.sent_at)) < 3600000;
      });
      return {
        group: g.group,
        fast: logs.length > 0 ? Math.round(fast.length / logs.length * 100) : 0,
        lt24:  logs.length > 0 ? Math.round(opened.length / logs.length * 100) : 0,
      };
    }).filter(Boolean);
  }, [emailLogs]);

  useEffect(() => {
    api.getEmailLogs()
      .then(data => {
        if (data && typeof data === 'object' && data.logs) {
          // New structured response
          setEmailLogs(Array.isArray(data.logs) ? data.logs.slice(0, 50) : []);
          if (Array.isArray(data.devices) && data.devices.length > 0) setDevicesData(data.devices);
          if (Array.isArray(data.monthly) && data.monthly.length > 0) setMonthlyData(data.monthly);
          if (data.tracking_summary) setTrackingSummary(data.tracking_summary);
        } else {
          // Legacy flat array
          setEmailLogs(Array.isArray(data) ? data.slice(0, 50) : []);
        }
      })
      .catch(() => setEmailLogs([]))
      .finally(() => setLogsLoading(false));
  }, []);

  /* ── byDay : taux d'ouverture réel par jour de semaine ── */
  const { byDay, hasRealByDay } = useMemo(() => {
    const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const counts = Array(7).fill(0).map(() => ({ sent: 0, opened: 0 }));
    emailLogs.forEach(log => {
      if (!log.sent_at) return;
      const dow = new Date(log.sent_at).getDay();
      counts[dow].sent++;
      if (log.opened_at || log.status === 'opened') counts[dow].opened++;
    });
    const result = DAY_LABELS.map((day, i) => ({
      day,
      rate: counts[i].sent > 0 ? Math.round(counts[i].opened / counts[i].sent * 100) : 0,
    }));
    const hasData = counts.some(c => c.sent > 0);
    return { byDay: result, hasRealByDay: hasData };
  }, [emailLogs]);

  /* Benchmark secteur si pas de données réelles */
  const byDayDisplay = hasRealByDay ? byDay : [
    { day: 'Lun', rate: 52 }, { day: 'Mar', rate: 71 }, { day: 'Mer', rate: 58 },
    { day: 'Jeu', rate: 63 }, { day: 'Ven', rate: 55 }, { day: 'Sam', rate: 34 }, { day: 'Dim', rate: 28 },
  ];

  /* ── byHour : taux d'ouverture réel par heure ── */
  const { byHour, hasRealByHour } = useMemo(() => {
    const HOURS = [6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21];
    const counts = {};
    HOURS.forEach(h => { counts[h] = { sent: 0, opened: 0 }; });
    emailLogs.forEach(log => {
      if (!log.sent_at) return;
      const h = new Date(log.sent_at).getHours();
      if (counts[h]) {
        counts[h].sent++;
        if (log.opened_at || log.status === 'opened') counts[h].opened++;
      }
    });
    const result = HOURS.map(h => ({
      h: `${h}h`,
      rate: counts[h].sent > 0 ? Math.round(counts[h].opened / counts[h].sent * 100) : 0,
    }));
    const hasData = Object.values(counts).some(c => c.sent > 0);
    return { byHour: result, hasRealByHour: hasData };
  }, [emailLogs]);

  /* Benchmark secteur si pas de données réelles */
  const byHourDisplay = hasRealByHour ? byHour : [
    { h: '6h', rate: 8 },  { h: '7h', rate: 22 }, { h: '8h', rate: 41 },
    { h: '9h', rate: 64 }, { h: '10h', rate: 78 }, { h: '11h', rate: 71 },
    { h: '12h', rate: 49 }, { h: '13h', rate: 55 }, { h: '14h', rate: 68 },
    { h: '15h', rate: 62 }, { h: '16h', rate: 57 }, { h: '17h', rate: 43 },
    { h: '18h', rate: 35 }, { h: '19h', rate: 27 }, { h: '20h', rate: 18 },
    { h: '21h', rate: 11 },
  ];

  const byRegion = [
    { region: 'Genève',    opens: 89, rate: 72, canton: 'GE' },
    { region: 'Sion',      opens: 67, rate: 81, canton: 'VS' },
    { region: 'Lausanne',  opens: 76, rate: 68, canton: 'VD' },
    { region: 'Martigny',  opens: 41, rate: 75, canton: 'VS' },
    { region: 'Sierre',    opens: 38, rate: 69, canton: 'VS' },
    { region: 'Monthey',   opens: 29, rate: 73, canton: 'VS' },
    { region: 'Berne',     opens: 38, rate: 61, canton: 'BE' },
    { region: 'Zürich',    opens: 34, rate: 58, canton: 'ZH' },
    { region: 'Fribourg',  opens: 24, rate: 55, canton: 'FR' },
    { region: 'Nendaz',    opens: 19, rate: 64, canton: 'VS' },
  ].sort((a, b) => b.rate - a.rate);

  const DEVICE_COLORS = { '📱 Mobile': VIOLET, '🖥 Desktop': CYAN, '📲 Tablette': YELLOW };
  // N'utilise les vraies données que si au moins un appareil a été détecté
  const hasRealDeviceData = devicesData && devicesData.some(d => (d.count ?? d.value) > 0);
  const devices = hasRealDeviceData ? devicesData.map(d => ({
    ...d,
    color: DEVICE_COLORS[d.name] ?? VIOLET,
  })) : [];

  const statusStyle = {
    opened:  { bg: '#DCFCE7', color: '#166534', label: '✓ Ouvert' },
    sent:    { bg: '#DBEAFE', color: '#1E40AF', label: '✉ Envoyé' },
    bounced: { bg: '#FEE2E2', color: '#991B1B', label: '✗ Bounce' },
    failed:  { bg: '#FEE2E2', color: '#991B1B', label: '✗ Erreur' },
    clicked: { bg: '#FEF9C3', color: '#854D0E', label: '🔗 Cliqué' },
  };

  const maxRegionRate = Math.max(...byRegion.map(r => r.rate));

  const activeWithEmail = Math.round(analytics.active_contacts * analytics.email_rate / 100);

  const emailsBannerText = (() => {
    const base = `${analytics.emails_sent_month || 0} e-mails envoyés ce mois — ${analytics.open_rate_month || 0}% de taux d'ouverture (moy. secteur 35%).`;
    if (hasRealByDay && hasRealByHour) {
      const bestDay = [...byDayDisplay].sort((a,b) => b.rate - a.rate)[0];
      const bestHr  = [...byHourDisplay].sort((a,b) => b.rate - a.rate)[0];
      return `${base} Meilleur créneau : ${bestDay?.day} à ${bestHr?.h} (${bestHr?.rate}% d'ouverture).`;
    }
    return base;
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {!hasAiCredits && <AiCreditBanner used={tenant?.ai_used} limit={tenant?.ai_limit} />}

      {/* ── Insights contextuels Email ── */}
      {(analytics.emails_sent_month > 0) && (() => {
        const openRate = analytics.open_rate_month || 0;
        const coverage = analytics.email_rate || 0;
        const insights = [];

        if (openRate >= 35)
          insights.push({ icon: '🏆', color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7',
            title: `Excellent — ${openRate}% de taux d'ouverture`,
            sub: `Au-dessus de la moyenne secteur (35%). Tes contacts lisent tes messages — continue sur cette lancée.`,
            cta: null });
        else if (openRate > 0)
          insights.push({ icon: '📈', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D',
            title: `${openRate}% d'ouverture — moyenne à 35%`,
            sub: `Améliore tes objets d'e-mail et envoie au meilleur créneau pour dépasser la moyenne secteur.`,
            cta: '/campaigns' });

        if (coverage < 70 && analytics.total_contacts > 0)
          insights.push({ icon: '📧', color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA',
            title: `${coverage}% de contacts ont un e-mail`,
            sub: `${Math.round((1 - coverage / 100) * (analytics.total_contacts || 0))} contacts sans adresse e-mail manquent tes campagnes. Enrichis leurs fiches.`,
            cta: '/contacts' });

        if (hasRealByDay) {
          const best = [...byDayDisplay].sort((a, b) => b.rate - a.rate)[0];
          if (best?.rate > 0)
            insights.push({ icon: '⏰', color: '#0369A1', bg: '#E0F2FE', border: '#7DD3FC',
              title: `Meilleur créneau : ${best.day} (${best.rate}% ouverture)`,
              sub: `Tes contacts ouvrent davantage le ${best.day}. Programme tes prochaines campagnes ce jour-là pour maximiser l'impact.`,
              cta: '/campaigns/new' });
        }

        if (insights.length === 0) return null;
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${insights.length}, 1fr)`, gap: 12 }}>
            {insights.map((ins, i) => (
              <div key={i} style={{
                background: ins.bg, border: `1.5px solid ${ins.border}`,
                borderRadius: 14, padding: '16px 18px',
                display: 'flex', gap: 14, alignItems: 'flex-start',
              }}>
                <div style={{ fontSize: 28, flexShrink: 0, lineHeight: 1, marginTop: 2 }}>{ins.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: ins.color, marginBottom: 5, lineHeight: 1.3 }}>{ins.title}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.55 }}>{ins.sub}</div>
                  {ins.cta && (
                    <Link to={ins.cta} style={{ display: 'inline-block', marginTop: 9, fontSize: 12, fontWeight: 700, color: ins.color, textDecoration: 'none' }}>
                      Voir →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="analytics-kpi-grid">
        <StatCard label="Envoyés ce mois"  value={analytics.emails_sent_month}                          icon="📤" color="violet" />
        <StatCard label="Ouverts ce mois"  value={analytics.emails_opened_month} sub={`${analytics.open_rate_month}% du total`} icon="👁"  color="green" />
        <StatCard label="Taux d'ouverture" value={`${analytics.open_rate_month}%`} sub="Moy. secteur : 35%"                     icon="📊" color="yellow" />
        <StatCard label="Feedbacks"        value={analytics.feedback_month}       sub="Ce mois"                                 icon="💛" color="pink" />
      </div>

      {/* Meilleur moment */}
      <div className="overview-grid">
        <div className="ov-card">
          <div className="ov-card-title">
            📅 Meilleur moment d'envoi
            {!hasRealByDay && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', marginLeft: 8, background: 'var(--line)', borderRadius: 20, padding: '2px 8px' }}>Benchmark secteur</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {byDayDisplay.map(d => (
              <div key={d.day} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, width: 30, color: d.rate >= 65 ? VIOLET : 'var(--muted)' }}>{d.day}</span>
                <div style={{ flex: 1, height: 8, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${d.rate}%`, background: d.rate >= 65 ? VIOLET : d.rate >= 50 ? CYAN : '#D1D5DB', borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, width: 34, textAlign: 'right', color: d.rate >= 65 ? VIOLET : 'var(--muted)' }}>{d.rate}%</span>
              </div>
            ))}
          </div>
          {hasRealByDay ? (() => {
            const best = [...byDayDisplay].sort((a,b) => b.rate - a.rate)[0];
            const worst = [...byDayDisplay].sort((a,b) => a.rate - b.rate)[0];
            return <>
              <AnalyseBox><strong>Analyse :</strong> Votre meilleur jour est le <strong>{best?.day}</strong> ({best?.rate}%). Le <strong>{worst?.day}</strong> obtient les résultats les plus faibles ({worst?.rate}%).</AnalyseBox>
              <ConseilBox><strong>Conseil :</strong> Programmez vos envois importants le <strong>{best?.day}</strong> pour maximiser l'ouverture.</ConseilBox>
            </>;
          })() : (
            <p className="chart-insight">📊 Ces données sont issues du benchmark secteur. Vos résultats réels apparaîtront après vos premiers envois trackés.</p>
          )}
          <Link to="/campaigns" className="ov-action-btn">Programmer mes envois →</Link>
        </div>

        <div className="ov-card">
          <div className="ov-card-title">📱 Appareils de lecture</div>
          {hasRealDeviceData ? (<>
            <div className="an-chart-area" style={{ margin: '0 14px' }}>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={devices} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={62} labelLine={false}>
                  {devices.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v}%`, n]} contentStyle={TOOLTIP_STYLE} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
                  formatter={(value, entry) => `${value} ${entry.payload.value}%`} />
              </PieChart>
            </ResponsiveContainer>
            </div>
            <p className="chart-insight">📱 {devices.find(d=>d.name.includes('Mobile'))?.value ?? 0}% de vos ouvertures se font sur mobile — vos emails doivent être parfaits sur smartphone.</p>
          </>) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 160, color: 'var(--muted)', textAlign: 'center', padding: '0 12px' }}>
              <span style={{ fontSize: 32 }}>📊</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Pas encore de données</span>
              <span style={{ fontSize: 12 }}>La répartition mobile / desktop / tablette apparaîtra ici après vos premiers envois trackés.</span>
            </div>
          )}
        </div>
      </div>

      {/* Évolution mensuelle + Heure */}
      <div className="analytics-row">
        <Card style={{ flex: 2 }}>
          <CardHeader title="📈 Évolution sur 5 mois" />
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={monthlyData || analytics.monthly_emails} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F8" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="sent"   name="Envoyés" stroke={VIOLET} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="opened" name="Ouverts"  stroke={GREEN}  strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          {(() => {
            const months = monthlyData || analytics.monthly_emails || [];
            if (months.length < 2) return <p className="chart-insight">📈 Continuez à envoyer pour voir votre tendance mensuelle apparaître ici.</p>;
            const last = months[months.length - 1];
            const prev = months[months.length - 2];
            const diff = last.sent - prev.sent;
            const peak = months.reduce((max, m) => m.sent > max.sent ? m : max, months[0]);
            return <p className="chart-insight">{diff > 0 ? '📈' : diff < 0 ? '📉' : '➡️'} {last.month} est <strong>{diff > 0 ? `en hausse de ${diff}` : diff < 0 ? `en baisse de ${Math.abs(diff)}` : 'stable'}</strong> envois vs {prev.month}. Pic : {peak.month} ({peak.sent} envois).</p>;
          })()}
        </Card>

        <Card style={{ flex: 1 }}>
          <CardHeader title="🕐 Pic par heure" subtitle={hasRealByHour ? "Taux d'ouverture" : "Taux d'ouverture — Benchmark secteur"} />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={byHourDisplay} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F8" vertical={false} />
              <XAxis dataKey="h" tick={{ fontSize: 9 }} tickLine={false} interval={1} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} domain={[0,100]} />
              <Tooltip formatter={v => [`${v}%`, "Taux d'ouverture"]} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="rate" radius={[3,3,0,0]} maxBarSize={22}>
                {byHourDisplay.map((d, i) => <Cell key={i} fill={d.rate >= 70 ? VIOLET : d.rate >= 55 ? CYAN : '#D1D5DB'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {hasRealByHour ? (() => {
            const best = [...byHourDisplay].sort((a,b) => b.rate - a.rate).slice(0,2);
            return <p className="chart-insight">⏰ Pic à <strong>{best[0]?.h} ({best[0]?.rate}%)</strong>{best[1] ? ` et ${best[1]?.h} (${best[1]?.rate}%)` : ''}.</p>;
          })() : (
            <p className="chart-insight">📊 Benchmark secteur — vos pics horaires réels apparaîtront après les premiers envois.</p>
          )}
        </Card>
      </div>

      {/* ── Heatmap d'engagement ── */}
      <div className="ov-card">
        <div className="ov-card-title">⊞ Heatmap d'engagement par créneau</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '2px', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ padding: '3px 8px', textAlign: 'right', color: 'var(--muted)', fontWeight: 700, fontSize: 10 }}>Heure</th>
                {EMAIL_DAYS.map(d => <th key={d} style={{ padding: '3px 6px', textAlign: 'center', color: 'var(--muted)', fontWeight: 700, fontSize: 10, minWidth: 32 }}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {EMAIL_HOURS.map(h => (
                <tr key={h}>
                  <td style={{ padding: '2px 8px', textAlign: 'right', color: 'var(--muted)', fontWeight: 600, fontSize: 10 }}>{h}h</td>
                  {EMAIL_DAYS.map(d => {
                    const val = heatmap[h]?.[d] || 0;
                    const intensity = val / heatmapMax;
                    const bg = val === 0 ? '#F3F4F6' : `rgba(245,158,11,${0.18 + intensity * 0.82})`;
                    const col = intensity > 0.55 ? '#fff' : intensity > 0 ? '#92400E' : '#D1D5DB';
                    return (
                      <td key={d} style={{
                        padding: '3px 2px', textAlign: 'center',
                        background: bg, borderRadius: 4,
                        color: col, fontWeight: val > 0 ? 700 : 400,
                        fontSize: 10,
                      }}>{val > 0 ? val : '–'}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, background: `${VIOLET}08`, border: `1px solid ${VIOLET}28`, borderRadius: 10, padding: '10px 14px', borderLeft: `3px solid ${VIOLET}` }}>
          <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 3 }}>🕐 Meilleurs créneaux identifiés</div>
          {bestSlot.h
            ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>Pic d'engagement : <strong>{bestSlot.d} à {bestSlot.h}h</strong></div>
            : <div style={{ fontSize: 12, color: 'var(--muted)' }}>Envoyez des emails pour voir les données d'engagement.</div>
          }
          <div style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic', marginTop: 4 }}>
            <em>Recommandation :</em> Programmez vos envois importants 1–2h avant les pics pour maximiser l'impact.
          </div>
        </div>
        <AnalyseBox>
          <strong>Analyse :</strong> {bestSlot.h ? `Pic d'engagement identifié : ${bestSlot.d} à ${bestSlot.h}h.` : `Pas encore de données d'ouverture — envoyez vos premières campagnes pour voir la heatmap.`}
        </AnalyseBox>
        {bestSlot.h && (
          <ConseilBox>
            <strong>Conseil :</strong> Synchronisez vos envois avec ces créneaux actifs — un email envoyé au bon moment génère 2× plus d'ouvertures qu'un envoi aléatoire.
          </ConseilBox>
        )}
        <Link to="/campaigns/new" className="ov-action-btn">Programmer une campagne →</Link>
      </div>

      {/* ── Vélocité d'engagement par âge ── */}
      <div className="ov-card">
        <div className="ov-card-title">⚡ Vélocité d'engagement par âge</div>
        {velocityData.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 160, color: 'var(--muted)', textAlign: 'center', padding: '0 12px' }}>
            <span style={{ fontSize: 32 }}>⚡</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Pas encore de données</span>
            <span style={{ fontSize: 12 }}>La vélocité d'ouverture par tranche d'âge apparaîtra ici après vos premiers envois trackés.</span>
          </div>
        ) : (<>
          <div className="an-chart-area" style={{ margin: '0 14px' }}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={velocityData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F8" vertical={false} />
              <XAxis dataKey="group" tick={{ fontSize: 12 }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} domain={[0, 100]} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(v, n) => [`${v}%`, n]}
              />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="fast"  name="Ouvertures < 1h"  fill={GREEN}  />
              <Bar dataKey="lt24"  name="Ouvertures < 24h" fill={YELLOW} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${velocityData.length}, 1fr)`, gap: 10, marginTop: 12, padding: '0 22px' }}>
            {velocityData.map(g => (
              <div key={g.group} style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--line)' }}>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10 }}>{g.group}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, textAlign: 'center' }}>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: GREEN }}>{g.fast}%</div>
                    <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2 }}>Ouvrent &lt; 1h</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: YELLOW }}>{g.lt24}%</div>
                    <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2 }}>Ouvrent &lt; 24h</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {(() => {
            const best = [...velocityData].sort((a,b) => b.fast - a.fast)[0];
            return <>
              <AnalyseBox><strong>Analyse :</strong> La tranche <strong>{best?.group}</strong> est la plus réactive ({best?.fast}% ouvrent en moins d'1h).</AnalyseBox>
              <ConseilBox><strong>Conseil :</strong> Segmentez vos envois par âge pour envoyer au bon moment selon les habitudes de chaque tranche d'âge.</ConseilBox>
            </>;
          })()}
          <Link to="/campaigns/new" className="ov-action-btn">Créer une campagne ciblée →</Link>
        </>)}
      </div>

      {/* Tracking summary — all-time stats */}
      {trackingSummary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 8 }}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{trackingSummary.total_sent}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>📊 Total envoyés</div>
          </div>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: GREEN }}>{trackingSummary.open_rate_alltime}%</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>👁 Taux ouverture (global)</div>
          </div>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#92400E' }}>{trackingSummary.total_clicked}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>🔗 Clics</div>
          </div>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#166534' }}>{trackingSummary.total_feedback}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>💛 Feedbacks</div>
          </div>
        </div>
      )}

      {/* Journal des envois */}
      <Card>
        <CardHeader
          title="📋 Journal des envois récents"
          subtitle={logsLoading ? 'Chargement…' : `${emailLogs.length} dernier${emailLogs.length !== 1 ? 's' : ''} envoi${emailLogs.length !== 1 ? 's' : ''}`}
        />
        {logsLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1,2,3,4,5].map(i => <div key={i} style={{ height: 70, borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--line)', animation: 'pulse 1.5s ease-in-out infinite' }} />)}
          </div>
        ) : emailLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
            Aucun envoi enregistré pour le moment.<br />
            <span style={{ fontSize: 12 }}>Les emails envoyés via Appee apparaîtront ici.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {emailLogs.map((log, idx) => {
              const openCount  = log.open_count ?? 0;
              const isOpened   = log.status === 'opened' || openCount > 0;
              const isFailed   = log.status === 'failed' || log.status === 'bounced';
              const isPending  = log.status === 'pending';

              const contactName = log.contact_name ?? '—';

              // Formatage dates
              const fmtDt = (iso) => {
                if (!iso) return null;
                const d = new Date(iso);
                return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')} à ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
              };
              const fmtTime = (iso) => {
                if (!iso) return null;
                const d = new Date(iso);
                return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
              };

              const sentAt    = fmtDt(log.sent_at);
              const openedAt  = fmtTime(log.opened_at);

              const device = log.device ?? null;
              const deviceIcon  = device === 'mobile' ? '📱' : device === 'tablet' ? '📲' : device === 'desktop' ? '🖥️' : '';
              const deviceLabel = device === 'mobile' ? 'mobile' : device === 'tablet' ? 'tablette' : device === 'desktop' ? 'ordinateur' : null;

              const subject = log.subject ?? log.template_name ?? log.campaign_name ?? null;

              // Couleurs & badge statut
              const isSent = log.status === 'sent';
              const statusBadge = isOpened
                ? { bg: '#DCFCE7', color: '#166534', text: `📬 Lu ${openCount}x` }
                : isFailed
                ? { bg: '#FEE2E2', color: '#991B1B', text: '✗ Échoué' }
                : isPending
                ? { bg: '#FEF9C3', color: '#92400E', text: '⏳ En attente' }
                : isSent
                ? { bg: '#DBEAFE', color: '#1E40AF', text: '📩 Envoyé' }
                : { bg: '#F3F4F6', color: '#6B7280', text: '✉ Non ouvert' };

              const leftBorder = isOpened ? GREEN : isFailed ? RED : isPending ? '#F59E0B' : '#D1D5DB';
              const bgColor    = isOpened ? '#F0FDF4' : isFailed ? '#FEF2F2' : '#fff';

              return (
                <div key={log.id ?? idx} style={{
                  borderRadius: 10,
                  border: `1px solid ${isOpened ? '#BBF7D0' : isFailed ? '#FECACA' : 'var(--line)'}`,
                  background: bgColor,
                  padding: '12px 14px',
                  borderLeft: `4px solid ${leftBorder}`,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    background: isOpened ? '#BBF7D0' : '#E5E7EB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, color: isOpened ? '#166534' : '#6B7280',
                  }}>
                    {(contactName || '?').charAt(0).toUpperCase()}
                  </div>

                  {/* Infos */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {log.contact_id
                        ? <ContactPreviewLink id={log.contact_id} name={contactName} />
                        : contactName
                      }
                      {/* Channel badge */}
                      {log.channel && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999,
                          background: log.channel === 'smtp' ? '#DBEAFE' : '#F3F4F6',
                          color: log.channel === 'smtp' ? '#1E40AF' : '#6B7280',
                        }}>
                          {log.channel === 'smtp' ? 'SMTP' : 'Mailjet'}
                        </span>
                      )}
                      {/* Feedback badge */}
                      {log.feedback_positive === true && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#DCFCE7', color: '#166534' }}>
                          💛 Apprécié
                        </span>
                      )}
                      {/* Click badge */}
                      {log.click_count > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#FEF3C7', color: '#92400E' }}>
                          🔗 x{log.click_count}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', flexWrap: 'wrap', gap: '3px 8px' }}>
                      {sentAt && <span>✈ Envoyé le {sentAt}</span>}
                      {subject && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>· {subject}</span>}
                      {isOpened && openedAt && (
                        <span style={{ color: GREEN, fontWeight: 600 }}>
                          · 1ère ouverture à {openedAt}{deviceIcon ? ` ${deviceIcon}` : ''}{deviceLabel ? ` (${deviceLabel})` : ''}
                        </span>
                      )}
                      {openCount > 1 && (
                        <span style={{ color: 'var(--muted)' }}>· relu {openCount - 1}x</span>
                      )}
                      {log.last_opened_at && log.last_opened_at !== log.opened_at && openCount > 1 && (
                        <span style={{ color: 'var(--muted)' }}>· Dernière ouverture : {fmtTime(log.last_opened_at)}</span>
                      )}
                    </div>
                  </div>

                  {/* Badge statut */}
                  <span style={{
                    background: statusBadge.bg, color: statusBadge.color,
                    fontSize: 11, fontWeight: 700, padding: '4px 10px',
                    borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    {statusBadge.text}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* TAB 5 — Opportunités                            */
/* ─────────────────────────────────────────────── */
/* ─────────────────────────────────────────────── */
/* MARKET OPPORTUNITIES BUILDER                    */
/* ─────────────────────────────────────────────── */
function getMarketOpportunities(companyProfile, analytics) {
  const raw = (companyProfile?.sector || companyProfile?.secteur || companyProfile?.industry || '').toLowerCase();
  const active = analytics.active_contacts || 0;
  const vipCount = analytics.vip_contacts || 0;

  const opps = [];

  // Universal: Local marketing
  if (active > 0) {
    opps.push({
      id: 'local_mkt', icon: '📍', color: CYAN,
      bg: 'linear-gradient(135deg,#ECFEFF,#CFFAFE)', border: '#A5F3FC',
      title: 'Marketing local & fidélisation',
      desc: `${active} contacts locaux à fidéliser avec des offres personnalisées`,
      detail: 'Estimé CHF 15/contact actif par campagne',
      value: Math.round(active * 15),
      action: 'Créer une campagne locale', link: '/campaigns/new',
    });
  }

  // Universal: Seasonal
  if (active > 5) {
    opps.push({
      id: 'seasonal', icon: '🗓️', color: VIOLET,
      bg: 'linear-gradient(135deg,#EDE9FF,#DDD6FE)', border: '#A78BFA',
      title: 'Campagnes saisonnières',
      desc: 'Fêtes, rentrée, été — des moments clés pour booster l\'engagement',
      detail: 'Estimé CHF 25/contact actif par saison',
      value: Math.round(active * 25),
      action: 'Planifier mes campagnes', link: '/campaigns/new',
    });
  }

  // Sector-specific
  if (raw.match(/coiff|esth[eé]|beaut|salon|barber/)) {
    opps.push({
      id: 'loyalty_beauty', icon: '💅', color: PINK,
      bg: 'linear-gradient(135deg,#FFF0F6,#FCE7F3)', border: '#F9A8D4',
      title: 'Programme fidélité beauté',
      desc: 'Offre exclusive sur la visite d\'anniversaire — taux de retour moyen 70%',
      detail: 'Estimé CHF 45/client/an',
      value: Math.round(active * 45),
      action: 'Créer le programme', link: '/templates',
    });
  }

  if (raw.match(/restaurant|gastr|caf[eé]|boulang|p[aâ]tisserie|traiteur|bistro|brasserie/)) {
    opps.push({
      id: 'table_birthday', icon: '🍽️', color: YELLOW,
      bg: 'linear-gradient(135deg,#FFFBEB,#FEF3C7)', border: '#FCD34D',
      title: 'Expériences anniversaire',
      desc: 'Table et menu surprise pour les habitués — engagement émotionnel maximal',
      detail: 'Estimé CHF 35/client/an · Taux de conversion 68%',
      value: Math.round(active * 35),
      action: 'Préparer les messages', link: '/templates',
    });
  }

  if (raw.match(/immob|r[eé]gie|promo.*immo|agence.*immo/)) {
    opps.push({
      id: 'market_watch', icon: '🏠', color: CYAN,
      bg: 'linear-gradient(135deg,#ECFEFF,#CFFAFE)', border: '#A5F3FC',
      title: 'Rapport marché personnalisé',
      desc: `Envoyez des analyses de marché ciblées à vos ${vipCount} prospects qualifiés`,
      detail: 'Estimé CHF 200/prospect qualifié',
      value: Math.round(vipCount * 200),
      action: 'Envoyer les rapports', link: '/campaigns/new',
    });
  }

  if (raw.match(/sport|fitness|gym|bien.?[eê]tre|yoga|pilates|kiné|physio/)) {
    opps.push({
      id: 'renewal', icon: '🏃', color: GREEN,
      bg: 'linear-gradient(135deg,#ECFDF5,#D1FAE5)', border: '#6EE7B7',
      title: 'Renouvellements proactifs',
      desc: 'Anticipez les fins d\'abonnement — message personnalisé 30 jours avant',
      detail: 'Estimé CHF 40/client/an · Réduction churn de 25%',
      value: Math.round(active * 40),
      action: 'Créer la séquence', link: '/campaigns/new',
    });
  }

  if (raw.match(/boutique|commerce|retail|magasin|vente|mode|bijou|cadeau/)) {
    opps.push({
      id: 'vip_sales', icon: '🛍️', color: YELLOW,
      bg: 'linear-gradient(135deg,#FFFBEB,#FEF3C7)', border: '#FCD34D',
      title: 'Soldes privées VIP',
      desc: `Accès anticipé aux promotions pour vos ${vipCount} clients VIP — exclusivité et fidélité`,
      detail: 'Estimé CHF 55/client VIP par événement',
      value: Math.round(vipCount * 55),
      action: 'Inviter mes VIPs', link: '/templates',
    });
  }

  if (raw.match(/m[eé]decin|sant[eé]|clinique|dentiste|optique|pharmacie|param[eé]dic/)) {
    opps.push({
      id: 'recall', icon: '🩺', color: GREEN,
      bg: 'linear-gradient(135deg,#ECFDF5,#D1FAE5)', border: '#6EE7B7',
      title: 'Rappels de suivi patient',
      desc: 'Relances automatiques pour les bilans et contrôles annuels',
      detail: 'Estimé CHF 30/patient/an · Taux de rappel +40%',
      value: Math.round(active * 30),
      action: 'Configurer les rappels', link: '/campaigns/new',
    });
  }

  return opps.filter(o => o.value > 0).slice(0, 4);
}

/* ─────────────────────────────────────────────── */
/* TAB 5 — Opportunités                            */
/* ─────────────────────────────────────────────── */
function TabOpportunites({ opportunities, milestones, totalOpportunity, tenant, companyProfile, contacts, analytics, aiInsights }) {
  const urgencyColor = (days) => days <= 14 ? RED : days <= 30 ? YELLOW : GREEN;
  const hasAiCredits = (tenant?.ai_used ?? 0) < (tenant?.ai_limit ?? Infinity);

  /* ── Market-based proposals ── */
  const marketOpps = useMemo(() => getMarketOpportunities(companyProfile, analytics), [companyProfile, analytics]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {!hasAiCredits && <AiCreditBanner used={tenant?.ai_used} limit={tenant?.ai_limit} />}

      {/* Banner total */}
      <div style={{ background: 'linear-gradient(135deg,#5A35FF,#7C3AED)', borderRadius: 18, padding: '24px 28px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 8px 32px rgba(90,53,255,.25)' }}>
        <div>
          <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Potentiel total identifié par Appee</div>
          <div style={{ fontSize: 42, fontWeight: 900, lineHeight: 1 }}>CHF {totalOpportunity.toLocaleString()}</div>
          <div style={{ fontSize: 13, opacity: 0.65, marginTop: 6 }}>{opportunities.length} opportunité{opportunities.length !== 1 ? 's' : ''} active{opportunities.length !== 1 ? 's' : ''} dans votre base — estimation conservatrice</div>
        </div>
        <div style={{ textAlign: 'center', opacity: 0.9 }}>
          <div style={{ fontSize: 52 }}>💰</div>
        </div>
      </div>

      {/* ── Opportunity cards ── */}
      <div className="overview-grid">
        {opportunities.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
            Toutes les opportunités ont été traitées — bravo !
          </div>
        ) : opportunities.map(o => (
          <div key={o.id} className="ov-card" style={{ background: o.bg, border: `1.5px solid ${o.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 32 }}>{o.icon}</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: o.color }}>CHF {o.value.toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>potentiel estimé</div>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 5 }}>{o.title}</div>
              <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6, marginBottom: 4 }}>{o.desc}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>{o.detail}</div>
            </div>
            <AiInsightBlock
              insights={aiInsights}
              cardKey={o.id}
              fallbackAnalyse={o.id === 'birthday'
                ? `Les occasions d'anniversaire génèrent en moyenne 3× plus de fidélité qu'un email promotionnel standard.`
                : o.id === 'vip'
                ? `Vos clients VIP représentent souvent 80% du chiffre d'affaires — une relation privilégiée se construit sur la durée.`
                : o.id === 'reactivation'
                ? `30% des clients dormants reviennent avec le bon message — ne les laissez pas partir définitivement.`
                : `Chaque contact sans e-mail est une opportunité marketing manquée — la collecte est un levier direct de croissance.`}
              fallbackConseil={o.id === 'vip'
                ? `Créez un message exclusif "Merci pour votre fidélité" pour renforcer le lien avec vos meilleurs clients.`
                : o.id === 'reactivation'
                ? `Une campagne de réactivation avec une offre limitée dans le temps génère en moyenne 30% de réponses.`
                : `Préparez vos messages à l'avance — un contact personnalisé augmente le taux d'engagement de 40%.`}
              fallbackLink={o.link}
              fallbackLinkLabel={o.action}
            />
            <Link to={o.link} className="ov-action-btn" style={{ background: o.color, color: o.id === 'vip' ? '#1F2937' : undefined }}>
              {o.action} →
            </Link>
          </div>
        ))}
      </div>

      {/* ── Propositions marché ── */}
      {marketOpps.length > 0 && (
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🎯</span> Propositions selon votre marché
            {companyProfile?.sector && (
              <span style={{ fontSize: 12, fontWeight: 600, background: '#EDE9FF', color: '#5A35FF', borderRadius: 20, padding: '2px 10px' }}>
                {companyProfile.sector}
              </span>
            )}
          </div>
          <div className="overview-grid">
            {marketOpps.map(o => (
              <div key={o.id} className="ov-card" style={{ background: o.bg, border: `1.5px solid ${o.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 32 }}>{o.icon}</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: o.color }}>CHF {o.value.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>potentiel estimé</div>
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 5 }}>{o.title}</div>
                  <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6, marginBottom: 4 }}>{o.desc}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>{o.detail}</div>
                </div>
                <Link to={o.link} className="ov-action-btn" style={{ background: o.color }}>
                  {o.action} →
                </Link>
              </div>
            ))}
            {!companyProfile?.sector && marketOpps.length === 0 && (
              <div style={{ gridColumn: '1 / -1', background: 'var(--bg)', border: '1px dashed var(--line)', borderRadius: 12, padding: '20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🏪</div>
                Complétez votre profil entreprise pour voir les opportunités spécifiques à votre secteur.
                <br />
                <Link to="/settings" style={{ color: '#5A35FF', fontWeight: 700, textDecoration: 'none', fontSize: 13 }}>Compléter mon profil →</Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Anniversaires ronds ── */}
      {milestones.length > 0 && (
        <div className="ov-card">
          <div className="ov-card-title">🎯 Anniversaires ronds à venir (50+)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
            {milestones.map(c => {
              const age = getAge(c.birth_date);
              const nextAge = age + 1;
              const [, m, d] = c.birth_date.split('-').map(Number);
              const now = new Date();
              let bday = new Date(now.getFullYear(), m - 1, d);
              if (bday < now) bday.setFullYear(now.getFullYear() + 1);
              const daysLeft = Math.round((bday - now) / (1000 * 3600 * 24));
              const col = urgencyColor(daysLeft);
              return (
                <Link to={`/contacts/${c.id}`} key={c.id} style={{ textDecoration: 'none' }}>
                  <div style={{ background: daysLeft <= 14 ? '#FFF7ED' : daysLeft <= 30 ? '#FFFBEB' : 'var(--bg)', border: `1.5px solid ${col}40`, borderRadius: 12, padding: '12px 14px', transition: 'box-shadow .15s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ background: col, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>{nextAge} ans</span>
                      <span style={{ fontSize: 12, color: col, fontWeight: 800 }}>J-{daysLeft}</span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{c.city} · {c.email ? '✉ OK' : '✉ manquant'}</div>
                    {c.category && <div style={{ marginTop: 4 }}><Badge variant="violet">{c.category}</Badge></div>}
                  </div>
                </Link>
              );
            })}
          </div>
          <AiInsightBlock
            insights={aiInsights}
            cardKey="milestones"
            fallbackAnalyse={`${milestones.length} anniversaire${milestones.length > 1 ? 's' : ''} rond${milestones.length > 1 ? 's' : ''} (50+) dans les 90 prochains jours — occasions relationnelles premium.`}
            fallbackConseil={`Préparez un message personnalisé avec une petite attention (offre exclusive, invitation, cadeau). Le taux d'engagement sur ces occasions est 4× supérieur à un email standard.`}
            fallbackLink="/birthdays"
            fallbackLinkLabel="Préparer les messages d'anniversaire"
          />
          <Link to="/birthdays" className="ov-action-btn">Préparer les messages d'anniversaire →</Link>
        </div>
      )}

    </div>
  );
}

/* ─────────────────────────────────────────────── */
/* TAB ALERTES — Notifications configurable        */
/* ─────────────────────────────────────────────── */

const ALERT_TYPES = [
  { id: 'rdv',             icon: '📅', label: 'Rappel RDV',           desc: 'Envoie un SMS/email de rappel avant chaque rendez-vous', unit: 'heures', defaultVal: 24, dir: 'fixed', isRdv: true },
  { id: 'crm_score',       icon: '💯', label: 'Score CRM',            desc: 'Alerte quand le score CRM passe sous le seuil',       unit: '/100', defaultVal: 60, dir: 'below' },
  { id: 'open_rate',       icon: '📧', label: "Taux d'ouverture",     desc: "Alerte si le taux d'ouverture mensuel est trop bas",  unit: '%',    defaultVal: 20, dir: 'below' },
  { id: 'sms_credits',     icon: '💳', label: 'Crédits SMS',          desc: 'Alerte quand il reste peu de crédits SMS',            unit: 'crédits', defaultVal: 10, dir: 'below' },
  { id: 'dormant',         icon: '😴', label: 'Contacts dormants',    desc: 'Alerte si trop de contacts sont inactifs',            unit: 'contacts', defaultVal: 10, dir: 'above' },
  { id: 'birthday_7d',     icon: '🎂', label: 'Anniversaires J-7',    desc: 'Rappel 7 jours avant chaque anniversaire',            unit: 'jours', defaultVal: 7, dir: 'fixed' },
  { id: 'no_campaign',     icon: '📣', label: 'Absence de campagne',  desc: "Alerte si aucune campagne n'a été envoyée depuis X jours", unit: 'jours', defaultVal: 30, dir: 'above' },
];

const LS_KEY = 'appee_alertes_v2';

function loadAlertes() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return ALERT_TYPES.map(t => ({
    id: t.id,
    enabled: false,
    threshold: t.defaultVal,
    recipients: [],
  }));
}

function saveAlertes(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch (_) {}
}

function newRecipient() {
  return { id: Date.now() + Math.random(), name: '', email: '', phone: '', channels: ['email'] };
}

function TabAlertes({ analytics, tenant }) {
  const [alertes, setAlertes] = useState(loadAlertes);
  const [expanded, setExpanded] = useState(null);
  const [saved, setSaved] = useState(false);

  /* current runtime values to show badge */
  const liveValues = {
    crm_score:   analytics?.crm_health      ?? 0,
    open_rate:   analytics?.open_rate_month ?? 0,
    sms_credits: tenant?.sms_credits        ?? 0,
    dormant:     analytics?.dormant_contacts ?? 0,
    birthday_7d: null,
    no_campaign: null,
  };

  function update(id, patch) {
    setAlertes(prev => {
      const next = prev.map(a => a.id === id ? { ...a, ...patch } : a);
      saveAlertes(next);
      return next;
    });
  }

  function addRecipient(alertId) {
    setAlertes(prev => {
      const next = prev.map(a => a.id === alertId
        ? { ...a, recipients: [...a.recipients, newRecipient()] }
        : a);
      saveAlertes(next);
      return next;
    });
  }

  function removeRecipient(alertId, recId) {
    setAlertes(prev => {
      const next = prev.map(a => a.id === alertId
        ? { ...a, recipients: a.recipients.filter(r => r.id !== recId) }
        : a);
      saveAlertes(next);
      return next;
    });
  }

  function updateRecipient(alertId, recId, patch) {
    setAlertes(prev => {
      const next = prev.map(a => a.id === alertId
        ? { ...a, recipients: a.recipients.map(r => r.id === recId ? { ...r, ...patch } : r) }
        : a);
      saveAlertes(next);
      return next;
    });
  }

  function toggleRecipientChannel(alertId, recId, ch) {
    setAlertes(prev => {
      const next = prev.map(a => {
        if (a.id !== alertId) return a;
        return {
          ...a,
          recipients: a.recipients.map(r => {
            if (r.id !== recId) return r;
            const has = r.channels.includes(ch);
            const channels = has
              ? r.channels.filter(c => c !== ch)
              : [...r.channels, ch];
            return { ...r, channels: channels.length ? channels : [ch] };
          })
        };
      });
      saveAlertes(next);
      return next;
    });
  }

  function handleSave() {
    saveAlertes(alertes);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  }

  const activeCount = alertes.filter(a => a.enabled).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Header ── */}
      <div className="an-alert-header">
        <div className="an-alert-header-left">
          <div className="an-alert-header-icon">🔔</div>
          <div>
            <div className="an-alert-header-title">Alertes & Notifications</div>
            <div className="an-alert-header-sub">
              {activeCount === 0
                ? 'Aucune alerte active — configure tes notifications ci-dessous'
                : `${activeCount} alerte${activeCount > 1 ? 's' : ''} active${activeCount > 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
        <button className="an-alert-save-btn" onClick={handleSave}>
          {saved ? '✅ Sauvegardé !' : '💾 Sauvegarder'}
        </button>
      </div>

      <SectionHint>
        Appee surveille ta base à ta place et te prévient <strong>au bon moment</strong> (crédits SMS bas, clients qui s'endorment, anniversaire à venir…). Active ce qui t'intéresse : tu n'as plus rien à surveiller, tu es alerté quand il faut agir.
      </SectionHint>

      {/* ── Alert cards ── */}
      {ALERT_TYPES.map(type => {
        const alerte = alertes.find(a => a.id === type.id) || { id: type.id, enabled: false, threshold: type.defaultVal, recipients: [] };
        const isOpen = expanded === type.id;
        const live = liveValues[type.id];
        const isTriggered = live !== null && (
          type.dir === 'below' ? live < alerte.threshold :
          type.dir === 'above' ? live > alerte.threshold : false
        );

        return (
          <div key={type.id} className={`an-alert-card ${alerte.enabled ? 'an-alert-card--on' : ''} ${isTriggered && alerte.enabled ? 'an-alert-card--triggered' : ''}`}>

            {/* ── Card top row ── */}
            <div className="an-alert-card-top" onClick={() => setExpanded(isOpen ? null : type.id)}>
              <div className="an-alert-card-icon">{type.icon}</div>
              <div className="an-alert-card-info">
                <div className="an-alert-card-label">
                  {type.label}
                  {isTriggered && alerte.enabled && (
                    <span className="an-alert-triggered-badge">⚠ Actif maintenant</span>
                  )}
                </div>
                <div className="an-alert-card-desc">{type.desc}</div>
              </div>
              <div className="an-alert-card-right">
                {live !== null && (
                  <span className="an-alert-live-val" style={{ color: isTriggered ? RED : GREEN }}>
                    {live}{type.unit !== 'jours' && type.unit !== 'contacts' ? type.unit : ''}
                  </span>
                )}
                {/* Toggle */}
                <button
                  className={`an-alert-toggle ${alerte.enabled ? 'an-alert-toggle--on' : ''}`}
                  onClick={e => { e.stopPropagation(); update(type.id, { enabled: !alerte.enabled }); }}
                  title={alerte.enabled ? 'Désactiver' : 'Activer'}
                >
                  <span className="an-alert-toggle-knob" />
                </button>
                <span className={`an-alert-chevron ${isOpen ? 'an-alert-chevron--open' : ''}`}>›</span>
              </div>
            </div>

            {/* ── Expanded panel ── */}
            {isOpen && (
              <div className="an-alert-panel">

                {/* ── RDV special block ── */}
                {type.isRdv && (
                  <div className="an-alert-rdv-block">
                    <div className="an-alert-rdv-top">
                      <div className="an-alert-rdv-badge">📅 Rappel automatique RDV</div>
                      <div className="an-alert-rdv-hint">
                        Appee envoie automatiquement un rappel à votre client avant chaque rendez-vous.
                      </div>
                    </div>
                    {/* Délai */}
                    <div className="an-alert-threshold-row">
                      <label className="an-alert-label">Envoyer le rappel</label>
                      <div className="an-alert-threshold-input-wrap">
                        <input
                          type="number"
                          className="an-alert-threshold-input"
                          value={alerte.threshold}
                          min={1}
                          onChange={e => update(type.id, { threshold: Number(e.target.value) })}
                        />
                        <span className="an-alert-threshold-unit">heures avant le RDV</span>
                      </div>
                    </div>
                    {/* Canaux globaux */}
                    <div className="an-alert-threshold-row">
                      <label className="an-alert-label">Canal d'envoi au client</label>
                      <div className="an-alert-channels">
                        {[
                          { id: 'sms',   label: '📱 SMS',   color: GREEN  },
                          { id: 'email', label: '✉ Email',  color: VIOLET },
                        ].map(ch => {
                          const active = (alerte.rdvChannels || ['sms']).includes(ch.id);
                          return (
                            <button
                              key={ch.id}
                              className={`an-alert-ch-btn ${active ? 'an-alert-ch-btn--on' : ''}`}
                              style={active ? { background: ch.color + '18', borderColor: ch.color, color: ch.color } : {}}
                              onClick={() => {
                                const cur = alerte.rdvChannels || ['sms'];
                                const has = cur.includes(ch.id);
                                const next = has ? cur.filter(c => c !== ch.id) : [...cur, ch.id];
                                update(type.id, { rdvChannels: next.length ? next : [ch.id] });
                              }}
                            >
                              {ch.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="an-alert-threshold-hint">
                        Le rappel sera envoyé au client via le(s) canal(aux) choisi(s) ci-dessus.
                      </div>
                    </div>
                    {/* Message SMS personnalisable */}
                    {(alerte.rdvChannels || ['sms']).includes('sms') && (
                      <div className="an-alert-threshold-row">
                        <label className="an-alert-label">Message SMS</label>
                        <textarea
                          className="an-alert-sms-text"
                          rows={3}
                          value={alerte.smsTemplate ?? "Bonjour {prénom}, rappel de votre RDV le {date} à {heure}. Pour modifier : {lien}"}
                          onChange={e => update(type.id, { smsTemplate: e.target.value })}
                        />
                        <div className="an-alert-sms-vars">
                          Variables disponibles :
                          {['{prénom}', '{date}', '{heure}', '{lien}'].map(v => (
                            <span key={v} className="an-alert-sms-var">{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Seuil (non-RDV et non-fixed) */}
                {type.dir !== 'fixed' && !type.isRdv && (
                  <div className="an-alert-threshold-row">
                    <label className="an-alert-label">
                      {type.dir === 'below' ? 'Seuil minimum' : 'Seuil maximum'}
                    </label>
                    <div className="an-alert-threshold-input-wrap">
                      <input
                        type="number"
                        className="an-alert-threshold-input"
                        value={alerte.threshold}
                        min={0}
                        onChange={e => update(type.id, { threshold: Number(e.target.value) })}
                      />
                      <span className="an-alert-threshold-unit">{type.unit}</span>
                    </div>
                    <div className="an-alert-threshold-hint">
                      {type.dir === 'below'
                        ? `Notification envoyée si la valeur passe sous ${alerte.threshold} ${type.unit}`
                        : `Notification envoyée si la valeur dépasse ${alerte.threshold} ${type.unit}`}
                    </div>
                  </div>
                )}

                {/* Recipients */}
                <div className="an-alert-recipients">
                  <div className="an-alert-section-title">
                    👥 Destinataires
                    <span className="an-alert-rec-count">{alerte.recipients.length}</span>
                  </div>

                  {alerte.recipients.length === 0 && (
                    <div className="an-alert-empty-rec">
                      Aucun destinataire — ajoutes-en un ci-dessous pour recevoir cette alerte.
                    </div>
                  )}

                  {alerte.recipients.map((rec, idx) => (
                    <div key={rec.id} className="an-alert-rec-row">
                      <div className="an-alert-rec-num">{idx + 1}</div>
                      <div className="an-alert-rec-fields">
                        <input
                          className="an-alert-input"
                          placeholder="Nom"
                          value={rec.name}
                          onChange={e => updateRecipient(type.id, rec.id, { name: e.target.value })}
                        />
                        <input
                          className="an-alert-input"
                          placeholder="Email"
                          type="email"
                          value={rec.email}
                          onChange={e => updateRecipient(type.id, rec.id, { email: e.target.value })}
                        />
                        <input
                          className="an-alert-input"
                          placeholder="Téléphone (SMS)"
                          type="tel"
                          value={rec.phone}
                          onChange={e => updateRecipient(type.id, rec.id, { phone: e.target.value })}
                        />
                        {/* Channel toggles */}
                        <div className="an-alert-channels">
                          {[
                            { id: 'email', label: '✉ Email',  color: VIOLET },
                            { id: 'sms',   label: '📱 SMS',   color: GREEN  },
                          ].map(ch => (
                            <button
                              key={ch.id}
                              className={`an-alert-ch-btn ${rec.channels.includes(ch.id) ? 'an-alert-ch-btn--on' : ''}`}
                              style={rec.channels.includes(ch.id) ? { background: ch.color + '18', borderColor: ch.color, color: ch.color } : {}}
                              onClick={() => toggleRecipientChannel(type.id, rec.id, ch.id)}
                            >
                              {ch.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button
                        className="an-alert-rec-remove"
                        onClick={() => removeRecipient(type.id, rec.id)}
                        title="Supprimer ce destinataire"
                      >✕</button>
                    </div>
                  ))}

                  <button className="an-alert-add-rec" onClick={() => addRecipient(type.id)}>
                    <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Ajouter un destinataire
                  </button>
                </div>

              </div>
            )}
          </div>
        );
      })}

      {/* ── Info footer ── */}
      <div className="an-alert-footer">
        <span style={{ fontSize: 16 }}>💡</span>
        <span>Les alertes sont envoyées automatiquement par Appee dès que le seuil est franchi. Les préférences sont sauvegardées localement.</span>
      </div>

    </div>
  );
}
